import React, { useRef, useState, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { generateItineraryPDF } from '../utils/pdfGenerator';
import {
  FaFilePdf,
  FaEdit,
  FaMapMarkerAlt,
  FaPlane,
  FaHeart,
  FaUtensils,
  FaCalendarAlt,
  FaCar,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaRoute,
  FaReceipt,
  FaUsers,
  FaDollarSign,
  FaCalculator,
  FaSave,
  FaTimes,
  FaStar,
  FaClipboardList,
  FaLandmark,
  FaDirections,
  FaSuitcase,
  FaMapPin,
  FaGlobe,
  FaChevronDown,
  FaExternalLinkAlt,
  FaClock,
  FaRegSquare,
  FaCheckSquare,
  FaSync,
  FaCompass,
  FaTh,
  FaList,
  FaPlus,
  FaTrash,
  FaCheck,
  FaChevronRight,
  FaLock,
  FaCrown,
  FaEnvelope,
  FaFileUpload
} from 'react-icons/fa';
import { getChatModifications, applyAllChatModifications, discardChatModification, updateMemberCount, translateItinerary, getFlights, getAttractionStatuses, updateAttractionStatus, reoptimizeItinerary, addWaypoint, swapItineraryDays, addAttractionToTrip, addQuickModification, getChecklistData, createChecklistSection, deleteChecklistSection, createChecklistItem, updateChecklistItem, toggleChecklistItemCheck, deleteChecklistItem, updateAIChecklistStatus, uploadChecklistItems, downloadChecklistTemplate } from '../api/client';
import ItineraryForm from './ItineraryForm';
import PhotoGallery from './PhotoGallery';
import Expenses from './Expenses';
import PackingList from './PackingList';
import TripTimeline from './TripTimeline';
import Chatbot from './Chatbot';
import GroupChat from './GroupChat';
import RatingFeedback from './RatingFeedback';
import RatingPromptModal from './RatingPromptModal';
import DayDirections from './DayDirections';
import DayWeather from './DayWeather';
import CurrencyCard from './CurrencyCard';
import ReviewLinks from './ReviewLinks';
import ConfirmDialog from './ConfirmDialog';
import FlightManager from './FlightManager';
import TripMembersList from './TripMembersList';
import { useToast, ToastContainer } from './Toast';
import { getRatingSummary, getWeatherForecast, getAdvisories, dismissAdvisory, refreshAdvisories } from '../api/client';
import TravelAdvisoryBanner from './TravelAdvisoryBanner';
import { extractLocationsWithCoordinates } from '../utils/itineraryParser';
import ExploreModal from './ExploreModal';
import AttractionCard from './AttractionCard';
import AffiliateDisclosure from './AffiliateDisclosure';
import PublicShareControl from './PublicShareControl';
import AttractionsPanel from './AttractionsPanel';
import { DayCardsGrid, DayDetailModal, extractDaysFromContent } from './dashboard';
import PageGuide from './PageGuide';
import SharePdfModal from './SharePdfModal';
import { isShortTrip } from '../utils/tripAccess';

// Supported languages for translation
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands' },
  { code: 'pl', name: 'Polish', native: 'Polski' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe' },
];

// Helper to get language name from code
const getLanguageName = (code) => {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang ? lang.name : 'English';
};

// Units of measure offered in the checklist "Add item" form (and matching the
// upload's optional Unit column). Empty string = no unit.
const CHECKLIST_UNIT_OPTIONS = [
  '', 'pcs', 'pack', 'box', 'set', 'pair', 'dozen', 'bottle', 'can', 'bag',
  'roll', 'lbs', 'oz', 'kg', 'g', 'L', 'ml', 'gallon', 'cup'
];

// Tiers that have access to group/expense features (Group plan and above)
const GROUP_FEATURE_TIERS = ['group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];
// All paid tiers
const PAID_TIERS = ['individual', ...GROUP_FEATURE_TIERS];
// Tiers with PDF email sharing access (Solo and above)
const PDF_EMAIL_TIERS = ['individual', ...GROUP_FEATURE_TIERS];

// Limit detailed itinerary markdown to the first day (Day 1 / Part 1) for the
// demo/free teaser — mirrors the locked day cards in the Cards view, so the
// Detailed view can't leak days that the Cards view keeps locked.
const sliceToFirstDayContent = (text) => {
  if (!text) return text;
  const lines = text.split('\n');
  const dayHeader = /^\s*(?:#{1,3}\s*)?\*?\*?\s*(?:Day|Part)\s+(\d+)\b/i;
  let seenDayHeaders = 0;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].trim().match(dayHeader);
    if (m) {
      seenDayHeaders += 1;
      if (parseInt(m[1], 10) >= 2 || seenDayHeaders >= 2) {
        return lines.slice(0, i).join('\n').trimEnd();
      }
    }
  }
  return text;
};

// Parse a "YYYY-MM-DD" date-only string as LOCAL midnight. Using `new Date(str)`
// on a date-only string parses it as UTC, which then collapses to the previous
// day for browsers west of UTC (all US timezones) once compared against a local
// `today` — making a trip look ended a day early. Parse the parts explicitly to
// stay in local time and match the backend's date-only comparison.
const parseLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

// Members keep full write/premium access for this many days AFTER end_date so the
// group can settle expenses, invite, re-optimize, etc. once home. MUST match backend
// SubscriptionService.MEMBER_ACCESS_GRACE_DAYS.
const MEMBER_ACCESS_GRACE_DAYS = 14;

// True if `endStr` (YYYY-MM-DD) plus `graceDays` is strictly before today (local).
// graceDays=0 is the literal "trip is over" check; a window keeps it false through grace.
const isTripWindowClosed = (endStr, graceDays = 0) => {
  const end = parseLocalDate(endStr);
  if (!end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(end);
  cutoff.setDate(cutoff.getDate() + graceDays);
  return cutoff < today;
};

const ItineraryDisplay = ({ itinerary, onReset, onRegenerate, onChatModificationApplied, isOwner = true, isMember = false, onViewPricing, onRefreshItinerary, currentUser, headerTitle, headerSubtitle, hideModify = false }) => {
  const userTier = currentUser?.subscription_tier || 'free';
  // Expenses tab visibility follows the itinerary owner's plan. A Solo-plan viewer
  // on a Group-plan owner's shared itinerary still sees the Expenses tab.
  const ownerTier = itinerary?.owner_subscription_tier || userTier;

  // A group member retains full access to a shared itinerary until 14 days after
  // the trip's end_date (matches backend is_trip_ended + MEMBER_ACCESS_GRACE_DAYS),
  // regardless of their own subscription. Owners remain gated by their own plan.
  const memberTripEnded = isTripWindowClosed(itinerary?.end_date, MEMBER_ACCESS_GRACE_DAYS);
  const memberHasFullAccess = isMember && !memberTripEnded;
  // Effective tier for feature gating: a covered member is treated as Group-plan.
  const effectiveTier = memberHasFullAccess ? 'group' : userTier;

  const hasExpenseAccess = GROUP_FEATURE_TIERS.includes(ownerTier) || GROUP_FEATURE_TIERS.includes(effectiveTier);
  const hasInviteAccess = GROUP_FEATURE_TIERS.includes(effectiveTier);
  const hasNumberOfTravellersAccess = GROUP_FEATURE_TIERS.includes(effectiveTier);
  const hasPaidAccess = PAID_TIERS.includes(effectiveTier);
  // Free/demo users get a Day-1 teaser in BOTH the Cards and Detailed views.
  const isFreeUser = !hasPaidAccess;
  const hasPdfEmailAccess = PDF_EMAIL_TIERS.includes(effectiveTier) || (currentUser?.pay_as_you_go && currentUser?.trip_credits > 0);
  const contentRef = useRef(null);
  const pdfContentRef = useRef(null);
  const pdfCaptureRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [chatModifications, setChatModifications] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [selectedChanges, setSelectedChanges] = useState({});
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [pendingChangesOpen, setPendingChangesOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('itinerary'); // 'itinerary' or 'expenses'
  const [editingMemberCount, setEditingMemberCount] = useState(false);
  const [memberCountValue, setMemberCountValue] = useState(itinerary?.member_count || 1);
  const [savingMemberCount, setSavingMemberCount] = useState(false);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [hasUserRated, setHasUserRated] = useState(false);
  const [cachedDirections, setCachedDirections] = useState(itinerary?.cached_directions || null);
  const [expandAllDirections, setExpandAllDirections] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [weatherData, setWeatherData] = useState(itinerary?.cached_weather || null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [showTranslateDropdown, setShowTranslateDropdown] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [tripFlights, setTripFlights] = useState([]);
  const [attractionStatuses, setAttractionStatuses] = useState({});
  const [isReoptimizing, setIsReoptimizing] = useState(false);
  const [reoptimizeError, setReoptimizeError] = useState(null);
  const [showReoptimizeConfirm, setShowReoptimizeConfirm] = useState(false);
  const [isSwappingDays, setIsSwappingDays] = useState(false);
  const [showSharePdfModal, setShowSharePdfModal] = useState(false);

  // Explore modal state
  const [exploreModalOpen, setExploreModalOpen] = useState(false);
  const [exploreStartLocation, setExploreStartLocation] = useState(null);
  const [exploreEndLocation, setExploreEndLocation] = useState(null);
  const [exploreStartCoords, setExploreStartCoords] = useState(null);
  const [exploreEndCoords, setExploreEndCoords] = useState(null);

  // Day cards grid state
  const [selectedDay, setSelectedDay] = useState(null);
  const [itineraryViewMode, setItineraryViewMode] = useState('cards'); // 'cards' or 'detailed'

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning, showInfo } = useToast();

  // Checklist state - synced with database for group sharing
  const [checkedItems, setCheckedItems] = useState({});
  const [checklistData, setChecklistData] = useState({
    customItems: {},
    removedItems: [],
    editedItems: {},
    customSections: [],
    customSectionIds: {},  // Maps section name to database ID
    customItemIds: {}      // Maps "sectionName:customId" to database ID
  });
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [expandedChecklistSections, setExpandedChecklistSections] = useState(new Set());
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [addingChecklistToSection, setAddingChecklistToSection] = useState(null);
  const [newChecklistItemText, setNewChecklistItemText] = useState('');
  const [newChecklistItemQty, setNewChecklistItemQty] = useState('');
  const [newChecklistItemUnit, setNewChecklistItemUnit] = useState('');
  const [showAddChecklistSection, setShowAddChecklistSection] = useState(false);
  const [newChecklistSectionName, setNewChecklistSectionName] = useState('');
  // Checklist file upload (CSV/Excel) state
  const [uploadingChecklist, setUploadingChecklist] = useState(false);
  const [checklistUploadErrors, setChecklistUploadErrors] = useState([]);
  const checklistFileInputRef = useRef(null);

  // Fetch checklist data from API (used on mount and after a bulk upload)
  const fetchChecklistData = useCallback(async () => {
      if (!itinerary?.request_id || !currentUser) return;
      setChecklistLoading(true);
      try {
        const data = await getChecklistData(itinerary.request_id);

        // Convert API format to local format for compatibility
        const customItems = {};
        const customSections = [];
        const customSectionIds = {};
        const customItemIds = {};
        const checkedState = {};
        const removedItems = [];
        const editedItems = {};

        // Process custom sections
        for (const section of data.custom_sections || []) {
          customSections.push(section.name);
          customSectionIds[section.name] = section.id;
        }

        // Process custom items
        for (const item of data.custom_items || []) {
          const sectionName = item.section_name;
          if (!customItems[sectionName]) customItems[sectionName] = [];
          const localId = `db_${item.id}`;
          customItems[sectionName].push({
            id: localId,
            text: item.text,
            user_name: item.user_name,
            member_code: item.member_code
          });
          customItemIds[`${sectionName}:${localId}`] = item.id;

          // Track checked state for custom items
          if (item.is_checked) {
            checkedState[`custom:${localId}`] = true;
          }
        }

        // Process AI item statuses (checked, removed, edited)
        for (const status of data.item_statuses || []) {
          if (status.is_checked) {
            checkedState[status.item_key] = true;
          }
          if (status.is_removed) {
            removedItems.push(status.item_key);
          }
          if (status.edited_text) {
            editedItems[status.item_key] = status.edited_text;
          }
        }

        setChecklistData({
          customItems,
          removedItems,
          editedItems,
          customSections,
          customSectionIds,
          customItemIds
        });
        setCheckedItems(checkedState);
      } catch (error) {
        console.error('Failed to fetch checklist data:', error);
        // Fall back to empty state
      } finally {
        setChecklistLoading(false);
      }
  }, [itinerary?.request_id, currentUser]);

  useEffect(() => {
    fetchChecklistData();
  }, [fetchChecklistData]);

  // Toggle check state for AI-generated items (syncs to database)
  const toggleChecklistItem = async (itemKey) => {
    const isCustom = itemKey.startsWith('custom:');
    const newCheckedState = !checkedItems[itemKey];

    // Optimistic update
    setCheckedItems(prev => {
      const updated = { ...prev, [itemKey]: newCheckedState };
      if (!newCheckedState) delete updated[itemKey];
      return updated;
    });

    try {
      if (isCustom) {
        // Extract custom item ID from itemKey (format: "custom:db_123")
        const localId = itemKey.replace('custom:', '');
        // Find the database ID from our mapping
        for (const [key, dbId] of Object.entries(checklistData.customItemIds || {})) {
          if (key.endsWith(`:${localId}`)) {
            await toggleChecklistItemCheck(itinerary.request_id, dbId);
            break;
          }
        }
      } else {
        // AI item - update status
        await updateAIChecklistStatus(itinerary.request_id, {
          item_key: itemKey,
          is_checked: newCheckedState
        });
      }
    } catch (error) {
      console.error('Failed to toggle checklist item:', error);
      // Revert on error
      setCheckedItems(prev => {
        const updated = { ...prev };
        if (newCheckedState) {
          delete updated[itemKey];
        } else {
          updated[itemKey] = true;
        }
        return updated;
      });
    }
  };

  const toggleChecklistSection = (sectionTitle) => {
    setExpandedChecklistSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionTitle)) next.delete(sectionTitle);
      else next.add(sectionTitle);
      return next;
    });
  };

  const handleAddChecklistItem = async (sectionTitle) => {
    const name = newChecklistItemText.trim();
    if (!name) return;

    // Compose "Item (Qty Unit)" — e.g. "Vegetable (5 lbs)". Qty/unit are optional.
    const qty = (newChecklistItemQty || '').toString().trim();
    const unit = (newChecklistItemUnit || '').trim();
    const qtyPart = qty ? (unit ? `${qty} ${unit}` : qty) : '';
    const text = qtyPart ? `${name} (${qtyPart})` : name;

    // Clear inputs immediately for better UX
    setNewChecklistItemText('');
    setNewChecklistItemQty('');
    setNewChecklistItemUnit('');
    setAddingChecklistToSection(null);

    try {
      // Check if this is a custom section with a database ID
      const sectionId = checklistData.customSectionIds?.[sectionTitle] || null;

      const response = await createChecklistItem(itinerary.request_id, {
        section_name: sectionTitle,
        section_id: sectionId,
        text: text
      });

      // Update local state with the new item
      const localId = `db_${response.id}`;
      setChecklistData(prev => ({
        ...prev,
        customItems: {
          ...prev.customItems,
          [sectionTitle]: [...(prev.customItems[sectionTitle] || []), {
            id: localId,
            text: response.text,
            user_name: response.user_name,
            member_code: response.member_code
          }]
        },
        customItemIds: {
          ...prev.customItemIds,
          [`${sectionTitle}:${localId}`]: response.id
        }
      }));
    } catch (error) {
      console.error('Failed to add checklist item:', error);
      showError('Failed to add item');
    }
  };

  const handleRemoveChecklistItem = async (sectionTitle, itemIndex, isCustom, customId) => {
    if (isCustom) {
      // Find the database ID for this custom item
      const dbId = checklistData.customItemIds?.[`${sectionTitle}:${customId}`];
      if (!dbId) return;

      // Optimistic update
      setChecklistData(prev => ({
        ...prev,
        customItems: {
          ...prev.customItems,
          [sectionTitle]: (prev.customItems[sectionTitle] || []).filter(item => item.id !== customId)
        }
      }));

      try {
        await deleteChecklistItem(itinerary.request_id, dbId);
      } catch (error) {
        console.error('Failed to delete checklist item:', error);
        // Could revert here, but deletion is usually final
      }
    } else {
      // AI item - mark as removed
      const itemKey = `${sectionTitle}:${itemIndex}`;

      // Optimistic update
      setChecklistData(prev => ({
        ...prev,
        removedItems: [...(prev.removedItems || []), itemKey]
      }));

      try {
        await updateAIChecklistStatus(itinerary.request_id, {
          item_key: itemKey,
          is_removed: true
        });
      } catch (error) {
        console.error('Failed to remove AI checklist item:', error);
        // Revert
        setChecklistData(prev => ({
          ...prev,
          removedItems: (prev.removedItems || []).filter(k => k !== itemKey)
        }));
      }
    }
  };

  const handleSaveChecklistEdit = async () => {
    if (!editingChecklistItem || !editingChecklistItem.text.trim()) return;
    const newText = editingChecklistItem.text.trim();
    const { section, index, isCustom, customId } = editingChecklistItem;

    setEditingChecklistItem(null);

    if (isCustom) {
      // Custom item - update in database
      const dbId = checklistData.customItemIds?.[`${section}:${customId}`];
      if (!dbId) return;

      // Optimistic update
      setChecklistData(prev => ({
        ...prev,
        customItems: {
          ...prev.customItems,
          [section]: (prev.customItems[section] || []).map(item =>
            item.id === customId ? { ...item, text: newText } : item
          )
        }
      }));

      try {
        await updateChecklistItem(itinerary.request_id, dbId, { text: newText });
      } catch (error) {
        console.error('Failed to update checklist item:', error);
      }
    } else {
      // AI item - save edited text
      const itemKey = `${section}:${index}`;

      // Optimistic update
      setChecklistData(prev => ({
        ...prev,
        editedItems: { ...(prev.editedItems || {}), [itemKey]: newText }
      }));

      try {
        await updateAIChecklistStatus(itinerary.request_id, {
          item_key: itemKey,
          edited_text: newText
        });
      } catch (error) {
        console.error('Failed to save AI checklist edit:', error);
      }
    }
  };

  const handleAddChecklistSection = async () => {
    if (!newChecklistSectionName.trim()) return;
    const name = newChecklistSectionName.trim();

    // Clear input immediately
    setNewChecklistSectionName('');
    setShowAddChecklistSection(false);

    try {
      const response = await createChecklistSection(itinerary.request_id, { name });

      // Update local state
      setChecklistData(prev => ({
        ...prev,
        customSections: [...(prev.customSections || []), name],
        customItems: { ...prev.customItems, [name]: [] },
        customSectionIds: { ...prev.customSectionIds, [name]: response.id }
      }));
      setExpandedChecklistSections(prev => new Set([...prev, name]));
    } catch (error) {
      console.error('Failed to add checklist section:', error);
      showError('Failed to add section');
    }
  };

  const handleRemoveChecklistSection = async (sectionTitle) => {
    const sectionId = checklistData.customSectionIds?.[sectionTitle];
    if (!sectionId) return;

    // Optimistic update
    setChecklistData(prev => ({
      ...prev,
      customSections: (prev.customSections || []).filter(s => s !== sectionTitle),
      customItems: { ...prev.customItems, [sectionTitle]: undefined }
    }));

    try {
      await deleteChecklistSection(itinerary.request_id, sectionId);
    } catch (error) {
      console.error('Failed to delete checklist section:', error);
    }
  };

  // Snapshot of the itinerary-generated (AI) checklist items currently visible,
  // with any user edits applied and removed items excluded. Sent with the upload
  // so the server can dedupe / update quantities across every category.
  const buildChecklistAIItemsSnapshot = () => {
    const snapshot = [];
    (checklistSections || []).forEach((section) => {
      (section.items || []).forEach((itemText, idx) => {
        const itemKey = `${section.title}:${idx}`;
        if ((checklistData.removedItems || []).includes(itemKey)) return;
        const text = checklistData.editedItems?.[itemKey] || itemText;
        snapshot.push({ item_key: itemKey, section_name: section.title, text });
      });
    });
    return snapshot;
  };

  const handleDownloadChecklistTemplate = async (format) => {
    try {
      const blob = await downloadChecklistTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'xlsx' ? 'checklist_template.xlsx' : 'checklist_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download checklist template:', error);
      showError('Could not download template. Please try again.');
    }
  };

  const handleChecklistFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    if (checklistFileInputRef.current) checklistFileInputRef.current.value = '';
    if (!file) return;

    const validExt = /\.(csv|xlsx|xlsm)$/i.test(file.name);
    if (!validExt) {
      setChecklistUploadErrors([{ message: 'Unsupported file type. Please upload a .csv or .xlsx file.' }]);
      showError('Unsupported file type');
      return;
    }

    setUploadingChecklist(true);
    setChecklistUploadErrors([]);
    try {
      const result = await uploadChecklistItems(
        itinerary.request_id,
        file,
        buildChecklistAIItemsSnapshot()
      );

      if (!result || result.success === false) {
        const errs = (result && result.errors) || [{ message: 'Upload failed. Please check your file and try again.' }];
        setChecklistUploadErrors(errs);
        showError('Please fix the errors and try again');
        return;
      }

      // Success — clear any prior errors and refresh so all items render.
      setChecklistUploadErrors([]);
      const parts = [];
      if (result.created) parts.push(`${result.created} added`);
      if (result.updated) parts.push(`${result.updated} updated`);
      if (result.skipped) parts.push(`${result.skipped} already present`);
      showSuccess(`Checklist uploaded${parts.length ? ': ' + parts.join(', ') : ''}`);
      setExpandedChecklistSections(prev => new Set([...prev, 'My checklist']));
      await fetchChecklistData();
    } catch (error) {
      console.error('Failed to upload checklist file:', error);
      const detail = error?.response?.data;
      if (detail && Array.isArray(detail.errors)) {
        setChecklistUploadErrors(detail.errors);
      } else {
        setChecklistUploadErrors([{ message: 'Upload failed. Please try again.' }]);
      }
      showError('Upload failed');
    } finally {
      setUploadingChecklist(false);
    }
  };

  const renderChecklistSection = (section, key, isCustomSection) => {
    const isExpanded = expandedChecklistSections.has(section.title);
    const customItems = checklistData.customItems?.[section.title] || [];
    const visibleOriginalCount = section.items.filter((_, idx) =>
      !(checklistData.removedItems || []).includes(`${section.title}:${idx}`)
    ).length;
    const allItemCount = visibleOriginalCount + customItems.length;
    let checkedCount = 0;
    section.items.forEach((_, idx) => {
      if (!(checklistData.removedItems || []).includes(`${section.title}:${idx}`) && checkedItems[`${section.title}:${idx}`]) checkedCount++;
    });
    customItems.forEach(ci => { if (checkedItems[`custom:${ci.id}`]) checkedCount++; });

    return (
      <div key={key} className="bg-neutral-50 rounded-xl border border-neutral-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-neutral-100 transition-colors" onClick={() => toggleChecklistSection(section.title)}>
          <div className="flex items-center gap-3">
            {isExpanded ? <FaChevronDown className="text-neutral-400 text-sm" /> : <FaChevronRight className="text-neutral-400 text-sm" />}
            <span className="font-semibold text-neutral-800">{section.title}</span>
            <span className="text-xs bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded-full">{checkedCount}/{allItemCount}</span>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setAddingChecklistToSection(section.title); setExpandedChecklistSections(prev => new Set([...prev, section.title])); }} className="text-neutral-400 hover:text-green-600 p-1 rounded transition-colors" title="Add item">
              <FaPlus className="text-sm" />
            </button>
            {isCustomSection && (
              <button onClick={() => handleRemoveChecklistSection(section.title)} className="text-neutral-400 hover:text-red-500 p-1 rounded transition-colors" title="Remove section">
                <FaTrash className="text-sm" />
              </button>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="px-4 pb-3 space-y-1">
            {section.items.map((itemText, idx) => {
              const itemKey = `${section.title}:${idx}`;
              if ((checklistData.removedItems || []).includes(itemKey)) return null;
              const displayText = checklistData.editedItems?.[itemKey] || itemText;
              const isChecked = !!checkedItems[itemKey];
              const isEditingThis = editingChecklistItem?.section === section.title && editingChecklistItem?.index === idx && !editingChecklistItem?.isCustom;
              if (isEditingThis) {
                return (
                  <div key={itemKey} className="flex items-center gap-2 py-1.5 px-3 bg-blue-50 rounded-lg">
                    <input type="text" value={editingChecklistItem.text} onChange={(e) => setEditingChecklistItem({ ...editingChecklistItem, text: e.target.value })} className="flex-1 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" autoFocus onKeyPress={(e) => e.key === 'Enter' && handleSaveChecklistEdit()} />
                    <button onClick={handleSaveChecklistEdit} className="text-green-600 hover:text-green-700 p-1"><FaCheck className="text-sm" /></button>
                    <button onClick={() => setEditingChecklistItem(null)} className="text-neutral-400 hover:text-neutral-600 p-1"><FaTimes className="text-sm" /></button>
                  </div>
                );
              }
              return (
                <div key={itemKey} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white group transition-colors">
                  <input type="checkbox" checked={isChecked} onChange={() => toggleChecklistItem(itemKey)} className="w-4 h-4 text-green-600 rounded border-neutral-300 focus:ring-green-500 cursor-pointer flex-shrink-0" />
                  <span className={`flex-1 text-sm ${isChecked ? 'line-through text-neutral-400' : 'text-neutral-700'}`}>{displayText}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditingChecklistItem({ section: section.title, index: idx, text: displayText, isCustom: false })} className="text-neutral-400 hover:text-blue-600 p-1" title="Edit"><FaEdit className="text-xs" /></button>
                    <button onClick={() => handleRemoveChecklistItem(section.title, idx, false)} className="text-neutral-400 hover:text-red-500 p-1" title="Remove"><FaTrash className="text-xs" /></button>
                  </div>
                </div>
              );
            })}
            {customItems.map((customItem) => {
              const itemKey = `custom:${customItem.id}`;
              const isChecked = !!checkedItems[itemKey];
              const isEditingThis = editingChecklistItem?.isCustom && editingChecklistItem?.customId === customItem.id;
              if (isEditingThis) {
                return (
                  <div key={itemKey} className="flex items-center gap-2 py-1.5 px-3 bg-blue-50 rounded-lg">
                    <input type="text" value={editingChecklistItem.text} onChange={(e) => setEditingChecklistItem({ ...editingChecklistItem, text: e.target.value })} className="flex-1 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" autoFocus onKeyPress={(e) => e.key === 'Enter' && handleSaveChecklistEdit()} />
                    <button onClick={handleSaveChecklistEdit} className="text-green-600 hover:text-green-700 p-1"><FaCheck className="text-sm" /></button>
                    <button onClick={() => setEditingChecklistItem(null)} className="text-neutral-400 hover:text-neutral-600 p-1"><FaTimes className="text-sm" /></button>
                  </div>
                );
              }
              return (
                <div key={itemKey} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white group transition-colors">
                  <input type="checkbox" checked={isChecked} onChange={() => toggleChecklistItem(itemKey)} className="w-4 h-4 text-green-600 rounded border-neutral-300 focus:ring-green-500 cursor-pointer flex-shrink-0" />
                  <span className={`flex-1 text-sm ${isChecked ? 'line-through text-neutral-400' : 'text-neutral-700'}`}>{customItem.text}</span>
                  {customItem.member_code && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0" title={`Added by ${customItem.user_name || 'Member'}`}>
                      {customItem.member_code}
                    </span>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditingChecklistItem({ section: section.title, customId: customItem.id, text: customItem.text, isCustom: true })} className="text-neutral-400 hover:text-blue-600 p-1" title="Edit"><FaEdit className="text-xs" /></button>
                    <button onClick={() => handleRemoveChecklistItem(section.title, null, true, customItem.id)} className="text-neutral-400 hover:text-red-500 p-1" title="Remove"><FaTrash className="text-xs" /></button>
                  </div>
                </div>
              );
            })}
            {visibleOriginalCount === 0 && customItems.length === 0 && addingChecklistToSection !== section.title && (
              <div className="text-center py-3 text-neutral-400 text-sm">No items. <button onClick={() => setAddingChecklistToSection(section.title)} className="text-green-600 hover:underline">Add one</button></div>
            )}
            {addingChecklistToSection === section.title && (
              <div className="flex items-center gap-2 py-1.5 px-3 bg-green-50 rounded-lg mt-1">
                <input type="text" value={newChecklistItemText} onChange={(e) => setNewChecklistItemText(e.target.value)} placeholder="New item..." className="flex-1 min-w-0 px-3 py-1.5 border border-green-300 rounded-lg text-sm focus:outline-none focus:border-green-500" autoFocus onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem(section.title)} />
                <input type="number" min="0" step="any" value={newChecklistItemQty} onChange={(e) => setNewChecklistItemQty(e.target.value)} placeholder="Qty" className="w-16 px-2 py-1.5 border border-green-300 rounded-lg text-sm focus:outline-none focus:border-green-500" onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem(section.title)} title="Quantity" />
                <select value={newChecklistItemUnit} onChange={(e) => setNewChecklistItemUnit(e.target.value)} className="w-24 px-2 py-1.5 border border-green-300 rounded-lg text-sm bg-white focus:outline-none focus:border-green-500" title="Unit of measure">
                  {CHECKLIST_UNIT_OPTIONS.map((u) => (
                    <option key={u || 'none'} value={u}>{u === '' ? 'unit' : u}</option>
                  ))}
                </select>
                <button onClick={() => handleAddChecklistItem(section.title)} className="text-green-600 hover:text-green-700 p-1"><FaCheck className="text-sm" /></button>
                <button onClick={() => { setAddingChecklistToSection(null); setNewChecklistItemText(''); setNewChecklistItemQty(''); setNewChecklistItemUnit(''); }} className="text-neutral-400 hover:text-neutral-600 p-1"><FaTimes className="text-sm" /></button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Scroll to top when component mounts or itinerary changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [itinerary?.request_id]);

  // Fetch attraction statuses
  useEffect(() => {
    const fetchAttractionStatuses = async () => {
      if (itinerary?.request_id && itinerary?.status === 'completed') {
        try {
          const response = await getAttractionStatuses(itinerary.request_id);
          if (response?.statuses) {
            // Convert array to object keyed by attraction_key for easy lookup
            const statusMap = {};
            response.statuses.forEach(s => {
              statusMap[s.attraction_key] = s;
            });
            setAttractionStatuses(statusMap);
          }
        } catch (error) {
          console.log('Failed to fetch attraction statuses:', error);
        }
      }
    };
    fetchAttractionStatuses();
  }, [itinerary?.request_id, itinerary?.status]);

  // Handle attraction status updates (visited, custom time)
  const handleAttractionStatusUpdate = async (dayNumber, attractionName, coords, updates) => {
    if (!itinerary?.request_id) return;

    // Create a unique key for this attraction
    const attractionKey = coords
      ? `${attractionName.substring(0, 50)}_${coords.lat.toFixed(4)}_${coords.lng.toFixed(4)}`
      : `${attractionName.substring(0, 50)}_${dayNumber}`;

    try {
      const statusData = {
        day_number: dayNumber,
        attraction_name: attractionName,
        attraction_key: attractionKey,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null,
        ...updates
      };

      const response = await updateAttractionStatus(itinerary.request_id, statusData);

      // Update local state
      setAttractionStatuses(prev => ({
        ...prev,
        [attractionKey]: response
      }));
    } catch (error) {
      console.error('Failed to update attraction status:', error);
    }
  };

  // Handle re-optimize
  const handleReoptimize = async () => {
    if (!itinerary?.request_id) return;

    setIsReoptimizing(true);
    setReoptimizeError(null);

    try {
      const result = await reoptimizeItinerary(itinerary.request_id, true);
      // The itinerary will be regenerated in the background
      // Show success message and reload the page to see updated status
      const message = result.excluded_count > 0
        ? `Re-optimization started! ${result.excluded_count} visited attraction(s) will be replaced with new suggestions. The page will reload to show progress.`
        : 'Re-optimization started! Your waypoints are being incorporated. The page will reload to show progress.';
      showSuccess(message, { duration: 3000 });
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Failed to re-optimize:', error);
      setReoptimizeError(error.response?.data?.detail || 'Failed to re-optimize itinerary');
      setIsReoptimizing(false);
    }
  };

  // Handle explore between two points
  const handleExplore = (startLocation, endLocation, startCoords, endCoords) => {
    setExploreStartLocation(startLocation);
    setExploreEndLocation(endLocation);
    setExploreStartCoords(startCoords);
    setExploreEndCoords(endCoords);
    setExploreModalOpen(true);
  };

  // Handle adding discovered place to itinerary
  const handleAddDiscoveredPlace = async (suggestion) => {
    try {
      // Add the suggestion as a waypoint with position context
      await addWaypoint(itinerary.request_id, {
        name: suggestion.name,
        lat: suggestion.lat,
        lng: suggestion.lng,
        description: suggestion.description,
        category: suggestion.category,
        insertAfterName: exploreStartLocation,
        insertBeforeName: exploreEndLocation
      });
      setExploreModalOpen(false);

      // Auto-trigger re-optimization to incorporate the waypoint
      showSuccess(`Adding "${suggestion.name}" to your trip and re-optimizing...`, { duration: 3000 });
      setIsReoptimizing(true);
      setReoptimizeError(null);

      try {
        await reoptimizeItinerary(itinerary.request_id, true);
        showSuccess(`"${suggestion.name}" has been added! The page will reload to show your updated itinerary.`, { duration: 3000 });
        setTimeout(() => window.location.reload(), 2000);
      } catch (reoptError) {
        console.error('Failed to re-optimize after adding waypoint:', reoptError);
        setReoptimizeError(reoptError.response?.data?.detail || 'Failed to re-optimize itinerary');
        setIsReoptimizing(false);
      }
    } catch (error) {
      console.error('Error adding waypoint:', error);
      showError(`Failed to add "${suggestion.name}" to your trip. Please try again.`);
    }
  };

  // Fetch flights for the trip
  useEffect(() => {
    const fetchTripFlights = async () => {
      if (itinerary?.request_id && itinerary?.status === 'completed') {
        try {
          const response = await getFlights(itinerary.request_id);
          if (response?.flights) {
            setTripFlights(response.flights);
          }
        } catch (error) {
          // Silently fail - flights are optional
          console.log('No flights found for this trip');
        }
      }
    };
    fetchTripFlights();
  }, [itinerary?.request_id, itinerary?.status]);

  // Sync member count value when itinerary changes
  useEffect(() => {
    if (itinerary?.member_count) {
      setMemberCountValue(itinerary.member_count);
    }
  }, [itinerary?.member_count]);

  // Sync cached directions when itinerary changes
  useEffect(() => {
    setCachedDirections(itinerary?.cached_directions || null);
  }, [itinerary?.cached_directions]);

  // Handler for when directions are cached
  const handleDirectionsCached = (newCachedDirections) => {
    setCachedDirections(newCachedDirections);
  };

  // Sync cached weather when itinerary changes
  useEffect(() => {
    setWeatherData(itinerary?.cached_weather || null);
  }, [itinerary?.cached_weather]);

  // Fetch weather forecast when itinerary is completed with a start_date
  useEffect(() => {
    const fetchWeather = async () => {
      if (
        itinerary &&
        itinerary.status === 'completed' &&
        itinerary.request_id &&
        itinerary.start_date
      ) {
        // Seed instantly from cached weather (if present) for a fast first paint.
        const hasCached = itinerary.cached_weather && itinerary.cached_weather.forecasts;
        if (hasCached) {
          setWeatherData(itinerary.cached_weather);
        }

        // Always validate with the backend, which returns the cached copy unless it's
        // stale or a seasonal estimate has entered the live forecast window (self-heal).
        if (!hasCached) setWeatherLoading(true);
        try {
          const data = await getWeatherForecast(itinerary.request_id);
          if (data && data.forecasts) {
            setWeatherData(data);
          }
        } catch (err) {
          console.error('Error fetching weather forecast:', err);
        } finally {
          setWeatherLoading(false);
        }
      }
    };

    fetchWeather();
  }, [itinerary?.status, itinerary?.request_id, itinerary?.start_date, itinerary?.cached_weather]);

  const fetchPendingChanges = async () => {
    if (itinerary && itinerary.request_id && isOwner) {
      try {
        const data = await getChatModifications(itinerary.request_id);
        const appliedMods = data.filter((mod) => mod.applied);
        const pending = data.filter((mod) => !mod.applied);
        setChatModifications(appliedMods);
        setPendingChanges(pending);
        // Auto-select all new pending changes
        setSelectedChanges((prev) => {
          const updated = { ...prev };
          pending.forEach((mod) => {
            if (!(mod.id in updated)) updated[mod.id] = true;
          });
          return updated;
        });
      } catch (error) {
        console.error('Error fetching chat modifications:', error);
      }
    }
  };

  useEffect(() => {
    fetchPendingChanges();
  }, [itinerary?.status, itinerary?.request_id, isOwner]);

  // Close translate dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTranslateDropdown && !event.target.closest('.relative')) {
        setShowTranslateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslateDropdown]);

  // Check if user has rated this itinerary and show prompt if not
  useEffect(() => {
    const checkRatingAndPrompt = async () => {
      if (itinerary && itinerary.status === 'completed' && itinerary.request_id) {
        // Check if trip has started (for rating to make sense)
        const tripStartDate = itinerary.start_date ? new Date(itinerary.start_date) : null;
        const now = new Date();
        const tripHasStarted = !tripStartDate || tripStartDate <= now;

        if (!tripHasStarted) return;

        // Check if we've already skipped this prompt
        const skipped = JSON.parse(localStorage.getItem('skippedRatingPrompts') || '[]');
        if (skipped.includes(itinerary.request_id)) return;

        try {
          const summary = await getRatingSummary(itinerary.request_id);
          setHasUserRated(!!summary.user_rating);

          // Show prompt if user hasn't rated and we haven't shown it recently
          if (!summary.user_rating) {
            // Small delay to let the page load first
            setTimeout(() => setShowRatingPrompt(true), 1500);
          }
        } catch (error) {
          console.error('Error checking rating:', error);
        }
      }
    };

    checkRatingAndPrompt();
  }, [itinerary?.request_id, itinerary?.status]);

  const handleModificationApplied = async (action) => {
    if (action === 'queued') {
      // Just refresh the pending changes list
      fetchPendingChanges();
      return;
    }
    if (onChatModificationApplied) {
      onChatModificationApplied();
    }
  };

  const handleApplyAllChanges = async () => {
    if (!itinerary?.request_id || pendingChanges.length === 0) return;
    const selected = pendingChanges.filter((m) => selectedChanges[m.id]);
    const unselected = pendingChanges.filter((m) => !selectedChanges[m.id]);
    if (selected.length === 0) {
      showError('Please select at least one change to apply');
      return;
    }
    setIsApplyingAll(true);
    try {
      // Discard unselected changes first
      await Promise.all(unselected.map((m) => discardChatModification(itinerary.request_id, m.id).catch(() => {})));
      // Apply all remaining (selected) changes
      await applyAllChatModifications(itinerary.request_id);
      setPendingChanges([]);
      setSelectedChanges({});
      if (onChatModificationApplied) onChatModificationApplied();
    } catch (error) {
      console.error('Error applying changes:', error);
      const detail = error.response?.data?.detail || 'Failed to apply changes';
      showError(detail);
    } finally {
      setIsApplyingAll(false);
    }
  };

  const handleToggleChange = (modId) => {
    setSelectedChanges((prev) => ({ ...prev, [modId]: !prev[modId] }));
  };

  const handleSelectAll = () => {
    const allSelected = pendingChanges.every((m) => selectedChanges[m.id]);
    const updated = {};
    pendingChanges.forEach((m) => { updated[m.id] = !allSelected; });
    setSelectedChanges((prev) => ({ ...prev, ...updated }));
  };

  const handleDiscardChange = async (modificationId) => {
    if (!itinerary?.request_id) return;
    try {
      await discardChatModification(itinerary.request_id, modificationId);
      setPendingChanges((prev) => prev.filter((m) => m.id !== modificationId));
    } catch (error) {
      console.error('Error discarding change:', error);
    }
  };

  const handleSwapDays = async (dayA, dayB) => {
    if (!itinerary?.request_id || isSwappingDays || !dayA || !dayB) return;
    setIsSwappingDays(true);
    try {
      await swapItineraryDays(itinerary.request_id, dayA, dayB);
      showSuccess(`Day ${dayA} and Day ${dayB} swapped!`);
      if (onRefreshItinerary) await onRefreshItinerary();
      const response = await getAttractionStatuses(itinerary.request_id);
      if (response?.statuses) {
        const statusMap = {};
        response.statuses.forEach(s => { statusMap[s.attraction_key] = s; });
        setAttractionStatuses(statusMap);
      }
    } catch (error) {
      console.error('Failed to swap days:', error);
      showError('Failed to swap days. Please try again.');
    } finally {
      setIsSwappingDays(false);
    }
  };

  const handleRemoveStop = async (dayNumber, stopName, stopLat, stopLng) => {
    if (!itinerary?.request_id) return;
    try {
      await addQuickModification(itinerary.request_id, 'remove_stop', dayNumber, stopName, stopLat, stopLng);
      // Refresh the pending changes list
      fetchPendingChanges();
    } catch (error) {
      console.error('Failed to add remove stop modification:', error);
      const detail = error.response?.data?.detail || 'Failed to add change. Please try again.';
      showError(detail);
      throw error; // Re-throw so caller knows it failed
    }
  };

  if (!itinerary) {
    return null;
  }

  const {
    request_id,
    status,
    start_location,
    end_location,
    interests,
    food_preference,
    start_date,
    end_date,
    transportation_mode,
    avoid_toll_roads,
    prefer_toll_roads,
    scenic_roads,
    waypoints,
    member_count = 1,
    invited_members_count = 0,
    total_expenses = 0,
    expense_currency = 'USD',
    itinerary: content,
    structured_itinerary,
    optimization_iterations,
    error_message
  } = itinerary;

  // Check if trip has started (current date >= start_date)
  const tripHasStarted = (() => {
    if (!start_date) return true; // If no start date, allow access
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    const tripStart = new Date(start_date);
    tripStart.setHours(0, 0, 0, 0);
    return today >= tripStart;
  })();

  // Check if trip is currently active (today >= start_date AND today <= end_date)
  const tripIsActive = (() => {
    if (!start_date || !end_date) return false; // Need both dates for timeline
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tripStart = parseLocalDate(start_date);
    const tripEnd = parseLocalDate(end_date);
    if (!tripStart || !tripEnd) return false;
    return today >= tripStart && today <= tripEnd;
  })();

  // Re-optimization stays available through the 14-day member grace window after
  // end_date (matches backend, which places no trip-end gate on this action).
  const tripHasEnded = isTripWindowClosed(end_date, MEMBER_ACCESS_GRACE_DAYS);

  // Extract days for the day cards grid
  let itineraryDays = [];
  try {
    itineraryDays = content ? extractDaysFromContent(content) : [];
  } catch (e) {
    console.error('extractDaysFromContent failed:', e);
  }

  // Non-paid accounts get the full itinerary experience on short trips (<= 3 days):
  // all days shown plus per-feature caps lifted. Longer trips keep the Day-1 teaser.
  const tripIsShort = isShortTrip(start_date, end_date, itineraryDays.length);
  const limitsWaived = isFreeUser && tripIsShort;

  // Parse checklist sections from AI content
  const checklistSections = content ? parseChecklistIntoSections(parseContentSections(content).checklistContent) : [];

  // Calculate expense per person
  const expensePerPerson = member_count > 0 ? total_expenses / member_count : 0;

  // Format currency
  const formatCurrency = (amount, currency) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', CAD: 'C$', AUD: 'A$' };
    const symbol = symbols[currency] || '$';
    return `${symbol}${amount.toFixed(2)}`;
  };


  const handleSaveMemberCount = async () => {
    if (memberCountValue < 1 || memberCountValue > 100) {
      showWarning('Member count must be between 1 and 100');
      return;
    }
    setSavingMemberCount(true);
    try {
      await updateMemberCount(request_id, memberCountValue);
      setEditingMemberCount(false);
      showSuccess('Member count updated successfully');
      // Trigger a refresh of the itinerary data
      if (onChatModificationApplied) {
        onChatModificationApplied();
      }
    } catch (error) {
      console.error('Error updating member count:', error);
      showError('Failed to update member count');
    } finally {
      setSavingMemberCount(false);
    }
  };

  const handleCancelMemberCountEdit = () => {
    setMemberCountValue(member_count);
    setEditingMemberCount(false);
  };

  const handleRegenerate = (formData) => {
    setIsEditing(false);
    if (onRegenerate) {
      onRegenerate(formData);
    }
  };

  const handleTranslate = async (targetLanguage) => {
    setShowTranslateDropdown(false);
    setIsTranslating(true);

    try {
      const result = await translateItinerary(itinerary.request_id, targetLanguage);
      if (result.success && onChatModificationApplied) {
        // Trigger a refresh to show the translated content
        onChatModificationApplied();
      }
    } catch (error) {
      console.error('Error translating itinerary:', error);
      showError('Failed to translate itinerary. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      // Use jsPDF directly for crystal-clear vector text - no html2canvas
      generateItineraryPDF(itinerary, { userTier: effectiveTier });
    } catch (err) {
      console.error('PDF generation error:', err);
      showError('Failed to generate PDF: ' + err.message);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // If editing, show the form
  if (isEditing) {
    return (
      <div className="animate-fade-in">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-neutral-800">Modify Your Itinerary</h2>
          <button
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 rounded-lg font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors"
          >
            Cancel
          </button>
        </div>
        <ItineraryForm
          onSubmit={handleRegenerate}
          loading={status === 'pending' || status === 'processing'}
          initialData={{
            request_id,
            start_location,
            end_location,
            interests,
            food_preference,
            start_date,
            end_date,
            transportation_mode,
            avoid_toll_roads,
            prefer_toll_roads,
            scenic_roads,
            waypoints
          }}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Explore Modal */}
      <ExploreModal
        isOpen={exploreModalOpen}
        onClose={() => setExploreModalOpen(false)}
        requestId={request_id}
        startLocation={exploreStartLocation}
        endLocation={exploreEndLocation}
        startCoords={exploreStartCoords}
        endCoords={exploreEndCoords}
        onAddToItinerary={handleAddDiscoveredPlace}
      />

      {/* Header with Actions */}
      <div className="bg-gradient-sunset rounded-2xl p-6 md:p-8 mb-8 text-white shadow-elevated">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{headerTitle || 'Your Travel Itinerary'}</h1>
            <p className="text-white/90">{headerSubtitle || 'Personalized just for you'}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {status === 'completed' && content && (
              <>
                {/* Modify button - only show for owners/members on trips that haven't started */}
                {!hideModify && (isOwner || isMember) && !tripHasStarted && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-white/30 transition-colors"
                  >
                    <FaEdit />
                    Modify
                  </button>
                )}
                {/* Translate Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowTranslateDropdown(!showTranslateDropdown)}
                    disabled={isTranslating}
                    className={`bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                      isTranslating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/30'
                    }`}
                  >
                    {isTranslating ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <FaGlobe />
                    )}
                    {isTranslating ? 'Translating...' : getLanguageName(itinerary.current_language || 'en')}
                    <FaChevronDown className={`transition-transform ${showTranslateDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showTranslateDropdown && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-elevated py-2 z-50 max-h-80 overflow-y-auto">
                      <div className="px-3 py-2 text-xs text-neutral-500 uppercase tracking-wide border-b border-neutral-100">
                        Select Language
                      </div>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleTranslate(lang.code)}
                          className="w-full px-4 py-2 text-left text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center justify-between"
                        >
                          <span>{lang.name}</span>
                          <span className="text-sm text-neutral-400">{lang.native}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className={`bg-white text-primary-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                    isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'
                  }`}
                >
                  <FaFilePdf className={isGeneratingPDF ? 'animate-pulse' : ''} />
                  {isGeneratingPDF ? 'Generating...' : 'PDF'}
                </button>
              </>
            )}
            <button
              onClick={onReset}
              className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors"
            >
              New Trip
            </button>
            <PageGuide pageKey="view-itinerary" variant="light" />
          </div>
        </div>
      </div>

      {/* Travel Advisory Banner - Show when itinerary is completed */}
      {status === 'completed' && request_id && (
        <TravelAdvisoryBanner requestId={request_id} />
      )}

      {/* Tab Navigation - Show when itinerary is completed */}
      {status === 'completed' && request_id && (
        <div className="mb-8">
          {/* Tab Bar */}
          <div className="bg-neutral-200 px-2 pt-2 rounded-t-xl">
            <div className="flex flex-wrap">
              <button
                onClick={() => setActiveTab('summary')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'summary'
                    ? 'text-indigo-600 bg-white rounded-t-lg border-t-4 border-indigo-500'
                    : 'text-neutral-600 hover:text-indigo-600'
                }`}
              >
                <FaGlobe className="text-lg" />
                <span className="hidden sm:inline">Summary</span>
              </button>
              <button
                onClick={() => setActiveTab('itinerary')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'itinerary'
                    ? 'text-primary-600 bg-white rounded-t-lg border-t-4 border-primary-500'
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                <FaRoute className="text-lg" />
                <span className="hidden sm:inline">Itinerary</span>
              </button>
              <button
                onClick={() => setActiveTab('flights')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'flights'
                    ? 'text-sky-600 bg-white rounded-t-lg border-t-4 border-sky-500'
                    : 'text-neutral-600 hover:text-sky-600'
                }`}
              >
                <FaPlane className="text-lg" />
                <span className="hidden sm:inline">Flights</span>
              </button>
              <button
                onClick={() => setActiveTab('checklist')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'checklist'
                    ? 'text-green-600 bg-white rounded-t-lg border-t-4 border-green-500'
                    : 'text-neutral-600 hover:text-green-600'
                }`}
              >
                <FaClipboardList className="text-lg" />
                <span className="hidden sm:inline">Checklist</span>
              </button>
              <button
                onClick={() => setActiveTab('attractions')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'attractions'
                    ? 'text-purple-600 bg-white rounded-t-lg border-t-4 border-purple-500'
                    : 'text-neutral-600 hover:text-purple-600'
                }`}
              >
                <FaLandmark className="text-lg" />
                <span className="hidden sm:inline">Attractions</span>
              </button>
              {/* Expenses tab — only visible when the itinerary owner's plan includes group features */}
              {hasExpenseAccess && (
                <button
                  onClick={() => setActiveTab('expenses')}
                  className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                    activeTab === 'expenses'
                      ? 'text-amber-600 bg-white rounded-t-lg border-t-4 border-amber-500'
                      : 'text-neutral-600 hover:text-amber-600'
                  }`}
                  title="Expenses"
                >
                  <FaReceipt className="text-lg" />
                  <span className="hidden sm:inline">Expenses</span>
                </button>
              )}
              {/* Timeline tab - only visible when there are invited members AND trip is active */}
              {invited_members_count > 0 && tripIsActive && (
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                    activeTab === 'timeline'
                      ? 'text-cyan-600 bg-white rounded-t-lg border-t-4 border-cyan-500'
                      : 'text-neutral-600 hover:text-cyan-600'
                  }`}
                >
                  <FaMapPin className="text-lg" />
                  <span className="hidden sm:inline">Timeline</span>
                </button>
              )}
              <button
                onClick={() => setActiveTab('rating')}
                className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                  activeTab === 'rating'
                    ? 'text-pink-600 bg-white rounded-t-lg border-t-4 border-pink-500'
                    : 'text-neutral-600 hover:text-pink-600'
                }`}
              >
                <FaStar className="text-lg" />
                <span className="hidden sm:inline">Rate Trip</span>
              </button>
            </div>
          </div>
          {/* Tab Content Area - Connected to tabs */}
          <div ref={pdfCaptureRef} className="bg-white rounded-b-xl shadow-medium p-6">
            {/* Summary Tab Content */}
            {activeTab === 'summary' && (
              <>
                {/* Trip Summary Section */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-neutral-800 mb-4 pb-3 border-b border-neutral-300 flex items-center gap-2">
                    <FaGlobe className="text-indigo-500" /> Trip Summary
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <FaMapMarkerAlt className="text-primary-500 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide">From</p>
                        <p className="font-semibold text-neutral-800">{start_location}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <FaPlane className="text-secondary-500 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide">To</p>
                        <p className="font-semibold text-neutral-800">{end_location}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <FaHeart className="text-primary-400 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Interests</p>
                        <div className="flex flex-wrap gap-2">
                          {interests.map((interest, idx) => (
                            <span
                              key={idx}
                              className="bg-primary-50 text-primary-700 px-2 py-1 rounded-md text-xs font-medium"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {food_preference && (
                      <div className="flex items-start gap-3">
                        <FaUtensils className="text-accent-500 text-xl mt-1" />
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Food Preference</p>
                          <p className="font-semibold text-neutral-800">{food_preference}</p>
                        </div>
                      </div>
                    )}

                    {start_date && (
                      <div className="flex items-start gap-3">
                        <FaCalendarAlt className="text-accent-500 text-xl mt-1" />
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Travel Dates</p>
                          <p className="font-semibold text-neutral-800">
                            {start_date}{end_date ? ` to ${end_date}` : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {transportation_mode && (
                      <div className="flex items-start gap-3">
                        <FaCar className="text-secondary-500 text-xl mt-1" />
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Transportation</p>
                          <p className="font-semibold text-neutral-800">
                            {transportation_mode}
                            {transportation_mode === 'Driving' && (avoid_toll_roads || prefer_toll_roads || scenic_roads) && (
                              <span className="text-sm text-neutral-600 block mt-1">
                                {[
                                  avoid_toll_roads && 'Avoid tolls',
                                  prefer_toll_roads && 'Prefer tolls',
                                  scenic_roads && 'Scenic routes'
                                ].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    {waypoints && waypoints.length > 0 && waypoints.some(w => w.city || w.name) && (
                      <div className="flex items-start gap-3">
                        <FaRoute className="text-indigo-500 text-xl mt-1" />
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Stops Along the Way</p>
                          <div className="flex flex-wrap gap-2">
                            {waypoints.filter(w => w.city || w.name).map((wp, idx) => (
                              <span
                                key={idx}
                                className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-medium"
                              >
                                {wp.city || wp.name}{wp.days ? ` (${wp.days} day${wp.days > 1 ? 's' : ''})` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Currency Reference Card - For international trips */}
                  <CurrencyCard requestId={request_id} startLocation={start_location} endLocation={end_location} />

                  {/* Expense Summary Section */}
                  <div className="mt-6 pt-6 border-t border-neutral-300">
                    <h3 className="text-sm font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                      <FaReceipt className="text-amber-500" /> Expense Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Total Expenses */}
                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <FaDollarSign className="text-amber-600" />
                          <p className="text-xs text-amber-700 uppercase tracking-wide font-medium">Total Expenses</p>
                        </div>
                        <p className="text-2xl font-bold text-amber-800">
                          {formatCurrency(total_expenses, expense_currency)}
                        </p>
                      </div>

                      {/* Number of Members */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <FaUsers className="text-blue-600" />
                          <p className="text-xs text-blue-700 uppercase tracking-wide font-medium">Number of Travelers</p>
                        </div>
                        {hasNumberOfTravellersAccess ? (
                          editingMemberCount ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={memberCountValue}
                                onChange={(e) => setMemberCountValue(parseInt(e.target.value) || 1)}
                                className="w-20 px-2 py-1 text-lg font-bold border-2 border-blue-300 rounded focus:outline-none focus:border-blue-500"
                                disabled={savingMemberCount}
                              />
                              <button
                                onClick={handleSaveMemberCount}
                                disabled={savingMemberCount}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors"
                                title="Save"
                              >
                                {savingMemberCount ? <FaSpinner className="animate-spin" /> : <FaSave />}
                              </button>
                              <button
                                onClick={handleCancelMemberCountEdit}
                                disabled={savingMemberCount}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                title="Cancel"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className="text-2xl font-bold text-blue-800">{member_count}</p>
                              {isOwner && (
                                <button
                                  onClick={() => {
                                    setMemberCountValue(member_count);
                                    setEditingMemberCount(true);
                                  }}
                                  className="p-1.5 text-blue-600 hover:bg-blue-200 rounded-full transition-colors"
                                  title="Edit member count"
                                >
                                  <FaEdit size={14} />
                                </button>
                              )}
                            </div>
                          )
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-2xl font-bold text-blue-800">{member_count}</p>
                            <span className="text-xs text-neutral-400 flex items-center gap-1"><FaLock className="text-[10px]" /> Group plan to edit</span>
                          </div>
                        )}
                      </div>

                      {/* Expense Per Person */}
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <FaCalculator className="text-green-600" />
                          <p className="text-xs text-green-700 uppercase tracking-wide font-medium">Per Person</p>
                        </div>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(expensePerPerson, expense_currency)}
                        </p>
                        {member_count > 1 && total_expenses > 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            View Expenses tab for settlement details
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trip Members Section */}
                  {request_id && (
                    <TripMembersList
                      requestId={request_id}
                      isOwner={isOwner}
                      hasInviteAccess={hasInviteAccess}
                    />
                  )}

                  {/* Share PDF Section */}
                  <div className="mt-6 pt-6 border-t border-neutral-300">
                    <h3 className="text-sm font-semibold text-neutral-700 mb-4 flex items-center gap-2">
                      <FaEnvelope className="text-primary-500" /> Share Itinerary
                    </h3>
                    <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
                      <p className="text-sm text-primary-800 mb-3">
                        Send a PDF version of your itinerary directly to travel companions via email.
                      </p>
                      {(hasPdfEmailAccess || limitsWaived) ? (
                        <button
                          onClick={() => setShowSharePdfModal(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-sm hover:shadow-md"
                        >
                          <FaFilePdf />
                          Email PDF Itinerary
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={onViewPricing}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded-lg font-medium hover:from-amber-500 hover:to-amber-600 transition-colors shadow-sm"
                          >
                            <FaCrown />
                            Upgrade to Solo
                          </button>
                          <span className="text-xs text-neutral-500">to unlock PDF email sharing</span>
                        </div>
                      )}

                      {/* The public link. A shared plan that a stranger can
                          actually open is the growth loop; an invite-only one
                          is a dead end. */}
                      <PublicShareControl
                        requestId={itinerary?.request_id}
                        isOwner={isOwner}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Itinerary Tab Content */}
            {activeTab === 'itinerary' && (
              <>
                {/* Compact Info Bar */}
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  {/* Left side - Status info */}
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {/* Status badge */}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {status === 'completed' ? 'Completed' : status}
                    </span>

                    {/* Optimization cycles */}
                    {optimization_iterations > 0 && (
                      <span className="text-neutral-500 text-xs">
                        {optimization_iterations} AI cycles
                      </span>
                    )}

                    {/* AI Modifications */}
                    {chatModifications.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <FaCheckCircle className="text-xs" />
                        {chatModifications.length} modification{chatModifications.length > 1 ? 's' : ''} applied
                      </span>
                    )}

                    {/* Hint for re-optimize */}
                    {Object.values(attractionStatuses).filter(s => s.visited).length === 0 && (
                      <span className="text-xs text-neutral-400">
                        Check off visited attractions below
                      </span>
                    )}
                  </div>

                  {/* Right side - Re-optimize button */}
                  <button
                    onClick={() => setShowReoptimizeConfirm(true)}
                    disabled={isReoptimizing || tripHasEnded || Object.values(attractionStatuses).filter(s => s.visited).length === 0}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                      isReoptimizing || tripHasEnded || Object.values(attractionStatuses).filter(s => s.visited).length === 0
                        ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                        : 'bg-sky-600 text-white hover:bg-sky-700 shadow-sm'
                    }`}
                    title={tripHasEnded
                      ? 'Cannot re-optimize - trip has ended'
                      : Object.values(attractionStatuses).filter(s => s.visited).length === 0
                        ? 'Mark attractions as visited first'
                        : `Re-optimize excluding ${Object.values(attractionStatuses).filter(s => s.visited).length} visited attraction(s)`
                    }
                  >
                    {isReoptimizing ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Re-optimizing...</span>
                      </>
                    ) : (
                      <>
                        <FaSync />
                        <span>Re-optimize</span>
                      </>
                    )}
                  </button>
                </div>

          {/* Pending Changes Panel */}
          {isOwner && pendingChanges.length > 0 && status !== 'processing' && (
            <div className="mb-6 rounded-xl overflow-hidden shadow-lg border border-amber-200 animate-fade-in">
              <button
                onClick={() => setPendingChangesOpen(!pendingChangesOpen)}
                className="w-full bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-3 flex items-center justify-between hover:from-amber-100 hover:to-orange-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <FaClipboardList className="text-amber-600 text-lg" />
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {pendingChanges.length}
                    </span>
                  </div>
                  <span className="font-semibold text-amber-800">
                    {pendingChanges.length} Pending Change{pendingChanges.length > 1 ? 's' : ''} to Review
                  </span>
                </div>
                <FaChevronDown className={`text-amber-600 transition-transform duration-200 ${pendingChangesOpen ? 'rotate-180' : ''}`} />
              </button>

              {pendingChangesOpen && (
                <div className="bg-white px-5 py-4">
                  <p className="text-sm text-neutral-500 mb-3">
                    Select the changes you want to include, then click <strong>Regenerate</strong> to update your itinerary.
                  </p>

                  {/* Select All / Deselect All */}
                  {pendingChanges.length > 1 && (
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium mb-2 flex items-center gap-1"
                    >
                      {pendingChanges.every((m) => selectedChanges[m.id]) ? (
                        <><FaRegSquare className="text-xs" /> Deselect All</>
                      ) : (
                        <><FaCheckSquare className="text-xs" /> Select All</>
                      )}
                    </button>
                  )}

                  <div className="space-y-2 mb-4">
                    {pendingChanges.map((mod) => (
                      <label
                        key={mod.id}
                        className={`flex items-start gap-3 rounded-lg p-3 cursor-pointer transition-colors border ${
                          selectedChanges[mod.id]
                            ? 'bg-primary-50/50 border-primary-200'
                            : 'bg-neutral-50 border-neutral-100 opacity-60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedChanges[mod.id]}
                          onChange={() => handleToggleChange(mod.id)}
                          className="mt-1 accent-primary-500 w-4 h-4 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${selectedChanges[mod.id] ? 'text-neutral-800' : 'text-neutral-500'}`}>
                            {mod.modification_description}
                          </p>
                          <p className="text-xs text-neutral-400 mt-1">
                            {new Date(mod.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); handleDiscardChange(mod.id); }}
                          className="w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
                          title="Remove this change"
                        >
                          <FaTimes className="text-xs text-white" />
                        </button>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    {tripHasEnded ? (
                      <div className="flex-1 bg-neutral-100 border border-neutral-300 text-neutral-600 font-medium py-3 px-6 rounded-lg flex items-center justify-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" />
                        Cannot regenerate - trip has ended
                      </div>
                    ) : (
                      <button
                        onClick={handleApplyAllChanges}
                        disabled={isApplyingAll || !pendingChanges.some((m) => selectedChanges[m.id])}
                        className="flex-1 bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg hover:from-primary-600 hover:to-accent-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isApplyingAll ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <FaSync />
                            Regenerate Itinerary ({pendingChanges.filter((m) => selectedChanges[m.id]).length} selected)
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content Section */}
          <div ref={contentRef}>
            {(status === 'pending' || (status === 'processing' && !content)) ? (
              <div className="bg-white rounded-xl shadow-medium p-12 text-center">
                <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                <h3 className="text-2xl font-bold text-neutral-800 mb-2">
                  {status === 'pending' ? 'Preparing Your Journey...' : 'Crafting Your Perfect Itinerary...'}
                </h3>
                <p className="text-neutral-600">
                  Our AI is optimizing your travel plan for the best experience
                </p>
              </div>
            ) : (status === 'completed' || (status === 'processing' && content)) && content ? (
              <div className="bg-white rounded-xl shadow-medium p-6 md:p-8 relative">
                {/* Regeneration Banner */}
                {status === 'processing' && (
                  <div className="mb-6 rounded-xl overflow-hidden shadow-lg animate-fade-in">
                    <div className="relative bg-gradient-to-r from-primary-600 via-accent-600 to-primary-600 bg-[length:200%_100%] animate-gradient-shift p-4">
                      <div className="flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <FaSync className="text-white text-lg animate-spin" style={{ animationDuration: '2s' }} />
                          </div>
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-golden-400 rounded-full animate-pulse"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-bold text-base">Updating Your Itinerary</h4>
                          <p className="text-white/85 text-sm mt-0.5">
                            Artie is incorporating your changes — your itinerary will refresh automatically when ready.
                          </p>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                          <span className="w-2 h-2 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                          <span className="w-2 h-2 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                          <span className="w-2 h-2 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                        </div>
                      </div>
                    </div>
                    <div className="h-1 bg-neutral-200">
                      <div className="h-full bg-gradient-to-r from-primary-500 via-accent-500 to-primary-500 animate-progress-bar"></div>
                    </div>
                  </div>
                )}

                <h2 className="text-2xl font-bold text-neutral-800 mb-6 pb-4 border-b-2 border-primary-500">
                  Your Personalized Itinerary
                </h2>

                {/* Directions Link - Show only if no flights are entered (user is driving/transit, not flying) */}
                {start_location && end_location && (!tripFlights || tripFlights.length === 0) && (
                  <div className="mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 shadow-md">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3 text-white">
                        <FaRoute className="text-2xl" />
                        <div>
                          <div className="font-bold text-lg">Get Directions</div>
                          <div className="text-sm text-white/90">{start_location} → {end_location}</div>
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(start_location)}&destination=${encodeURIComponent(end_location)}&travelmode=${transportation_mode === 'Driving' ? 'driving' : transportation_mode === 'Transit' ? 'transit' : 'driving'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-5 py-2.5 rounded-lg font-bold transition-colors shadow-md"
                      >
                        <FaCar className="text-lg" />
                        <span>Open in Google Maps</span>
                        <FaExternalLinkAlt size={12} />
                      </a>
                    </div>
                  </div>
                )}

                {/* Day Cards Grid or Detailed View */}
                {itineraryDays.length > 0 && (
                  <div className="mb-8">
                    {/* Header with view toggle */}
                    <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                          {itineraryViewMode === 'cards' ? <FaTh className="text-indigo-500" /> : <FaList className="text-indigo-500" />}
                          Your Journey
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {itineraryDays.length} {itineraryDays.length === 1 ? 'day' : 'days'} · {itineraryDays.reduce((acc, d) => acc + (d.locations?.length || 0), 0)} stops
                        </p>
                      </div>
                      {/* View Toggle */}
                      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                        <button
                          onClick={() => setItineraryViewMode('cards')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            itineraryViewMode === 'cards'
                              ? 'bg-white text-indigo-600 shadow-sm'
                              : 'text-slate-600 hover:text-slate-800'
                          }`}
                          title="Card view"
                        >
                          <FaTh className="text-xs" />
                          <span className="hidden sm:inline">Cards</span>
                        </button>
                        <button
                          onClick={() => setItineraryViewMode('detailed')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            itineraryViewMode === 'detailed'
                              ? 'bg-white text-indigo-600 shadow-sm'
                              : 'text-slate-600 hover:text-slate-800'
                          }`}
                          title="Detailed view with full descriptions"
                        >
                          <FaList className="text-xs" />
                          <span className="hidden sm:inline">Detailed</span>
                        </button>
                      </div>
                    </div>

                    {/* Card View */}
                    {itineraryViewMode === 'cards' && (
                      <DayCardsGrid
                        days={itineraryDays}
                        startDate={start_date}
                        weatherData={weatherData}
                        attractionStatuses={attractionStatuses}
                        onViewDay={(day) => setSelectedDay(day)}
                        onSwapDays={handleSwapDays}
                        isSwapping={isSwappingDays}
                        isOwner={isOwner}
                        userTier={effectiveTier}
                        limitsWaived={limitsWaived}
                        onViewPricing={onViewPricing}
                        destination={end_location}
                        bookingSurface={itinerary?.request_id ? `itin_${itinerary.request_id}` : undefined}
                        endDate={end_date}
                        transportationMode={transportation_mode}
                        onRemoveStop={handleRemoveStop}
                      />
                    )}

                    {/* Detailed View */}
                    {itineraryViewMode === 'detailed' && (() => {
                      const detailedContent = parseContentSections(content).mainContent;
                      // Non-paid users see only Day 1 here (like the Cards view) — but
                      // short trips (<= 3 days) are fully unlocked.
                      const lockDetailed = isFreeUser && !tripIsShort && itineraryDays.length > 1;
                      const lockedDayCount = itineraryDays.length - 1;
                      return (
                        <>
                          <FormattedContent
                            content={lockDetailed ? sliceToFirstDayContent(detailedContent) : detailedContent}
                            showDirections={true}
                            isOwner={isOwner}
                            destination={end_location}
                            requestId={request_id}
                            cachedDirections={cachedDirections}
                            onDirectionsCached={handleDirectionsCached}
                            expandAllDirections={expandAllDirections}
                            weatherData={weatherData}
                            weatherLoading={weatherLoading}
                            flights={tripFlights}
                            tripStartDate={start_date}
                            attractionStatuses={attractionStatuses}
                            onAttractionStatusUpdate={handleAttractionStatusUpdate}
                            showAttractionControls={true}
                            onExplore={handleExplore}
                          />
                          {lockDetailed && (
                            <div className="relative mt-4 rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden">
                              {/* Blurred placeholder for the locked days */}
                              <div className="opacity-60 grayscale blur-[2px] pointer-events-none select-none px-4 py-6 bg-gradient-to-b from-slate-50 to-white">
                                <div className="h-4 w-2/3 bg-slate-200 rounded mb-3" />
                                <div className="h-3 w-full bg-slate-100 rounded mb-2" />
                                <div className="h-3 w-5/6 bg-slate-100 rounded mb-2" />
                                <div className="h-3 w-4/6 bg-slate-100 rounded" />
                              </div>
                              {/* Upgrade overlay — mirrors the locked day cards */}
                              <div className="absolute inset-0 bg-slate-900/10 flex flex-col items-center justify-center text-center px-6">
                                <div className="bg-white/90 rounded-full p-3 shadow-lg mb-3">
                                  <FaLock className="text-slate-500 text-xl" />
                                </div>
                                <p className="text-slate-700 font-semibold mb-1">
                                  {lockedDayCount} more {lockedDayCount === 1 ? 'day' : 'days'} in this itinerary
                                </p>
                                <p className="text-slate-500 text-sm mb-3">
                                  Upgrade to unlock your full day-by-day plan.
                                </p>
                                <button
                                  onClick={onViewPricing}
                                  className="px-5 py-2 rounded-lg bg-sky-500 text-white font-semibold shadow-sm hover:bg-sky-600 transition-colors"
                                >
                                  Upgrade to unlock the full itinerary
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {/* Both views mount booking buttons, so the disclosure has
                        to sit on the page rather than only inside a dropdown. */}
                    {itineraryViewMode === 'detailed' && <AffiliateDisclosure className="mt-4" />}
                  </div>
                )}

                {/* Day Detail Modal */}
                <DayDetailModal
                  isOpen={selectedDay !== null}
                  onClose={() => setSelectedDay(null)}
                  day={selectedDay}
                  startDate={start_date}
                  weatherData={weatherData}
                  attractionStatuses={attractionStatuses}
                  onAttractionStatusUpdate={handleAttractionStatusUpdate}
                  flights={tripFlights}
                  isOwner={isOwner}
                  destination={end_location}
                  bookingSurface={itinerary?.request_id ? `itin_${itinerary.request_id}` : undefined}
                  onExplore={handleExplore}
                  onRemoveStop={handleRemoveStop}
                />

                {/* Show detailed itinerary only if no day cards are available */}
                {itineraryDays.length === 0 && (
                  <FormattedContent
                    content={parseContentSections(content).mainContent}
                    showDirections={true}
                    isOwner={isOwner}
                    destination={end_location}
                    requestId={request_id}
                    cachedDirections={cachedDirections}
                    onDirectionsCached={handleDirectionsCached}
                    expandAllDirections={expandAllDirections}
                    weatherData={weatherData}
                    weatherLoading={weatherLoading}
                    flights={tripFlights}
                    tripStartDate={start_date}
                    attractionStatuses={attractionStatuses}
                    onAttractionStatusUpdate={handleAttractionStatusUpdate}
                    showAttractionControls={true}
                    onExplore={handleExplore}
                  />
                )}

                {/* Re-optimize Section */}
                {/* Re-optimize Section - Bottom */}
                <div className={`mt-8 p-6 border rounded-xl ${
                  tripHasEnded
                    ? 'bg-gradient-to-r from-neutral-50 to-neutral-100 border-neutral-300'
                    : 'bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200'
                }`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className={`text-lg font-bold flex items-center gap-2 ${tripHasEnded ? 'text-neutral-500' : 'text-neutral-800'}`}>
                        <FaSync className={tripHasEnded ? 'text-neutral-400' : 'text-sky-600'} />
                        Re-optimize Itinerary
                      </h3>
                      <p className={`mt-1 ${tripHasEnded ? 'text-neutral-500' : 'text-neutral-600'}`}>
                        {tripHasEnded ? (
                          <>
                            <FaExclamationTriangle className="inline text-amber-500 mr-1" />
                            Re-optimization is not available for trips that have ended or end today.
                            This feature is only available for upcoming or ongoing trips with future dates.
                          </>
                        ) : Object.values(attractionStatuses).filter(s => s.visited).length > 0 ? (
                          <>
                            <span className="font-semibold text-sky-700">{Object.values(attractionStatuses).filter(s => s.visited).length}</span> attraction(s) marked as visited.
                            Re-optimize to get new suggestions for remaining days.
                          </>
                        ) : (
                          <>
                            Use the <FaCheckSquare className="inline text-green-600 mx-1" /> checkboxes next to attractions above to mark places you've already visited,
                            then click re-optimize to get fresh suggestions.
                          </>
                        )}
                      </p>
                      {reoptimizeError && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                          <FaExclamationTriangle />
                          {reoptimizeError}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowReoptimizeConfirm(true)}
                      disabled={isReoptimizing || tripHasEnded || Object.values(attractionStatuses).filter(s => s.visited).length === 0}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                        isReoptimizing || tripHasEnded || Object.values(attractionStatuses).filter(s => s.visited).length === 0
                          ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                          : 'bg-sky-600 text-white hover:bg-sky-700 shadow-md hover:shadow-lg'
                      }`}
                      title={tripHasEnded ? 'Cannot re-optimize - trip has ended' : ''}
                    >
                      {isReoptimizing ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Re-optimizing...
                        </>
                      ) : (
                        <>
                          <FaSync />
                          Re-optimize Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : status === 'failed' ? (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
                <FaExclamationTriangle className="text-red-500 text-5xl mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-red-800 mb-2">Generation Failed</h3>
                <p className="text-red-600 mb-6">{error_message || 'An error occurred while generating your itinerary.'}</p>
                <button
                  onClick={onReset}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : null}
          </div>

          {/* Photo Gallery - Available to owners and members */}
          {status === 'completed' && request_id && (
            <div className="mt-8">
              <PhotoGallery requestId={request_id} isOwner={isOwner} isMember={isMember} userTier={effectiveTier} limitsWaived={limitsWaived} onViewPricing={onViewPricing} />
            </div>
          )}
              </>
            )}

            {/* Checklist Tab Content (Combined: Checklist + Packing List) */}
            {activeTab === 'checklist' && status === 'completed' && (
              <>
                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-green-500">
                  <div className="flex items-center gap-3">
                    <FaClipboardList className="text-green-500 text-2xl" />
                    <h2 className="text-2xl font-bold text-neutral-800">Travel Checklist</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={checklistFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      onChange={handleChecklistFileSelected}
                    />
                    <button
                      onClick={() => { setChecklistUploadErrors([]); checklistFileInputRef.current?.click(); }}
                      disabled={uploadingChecklist}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-60"
                      title="Upload items from a CSV or Excel file (columns: Item, Qty, Unit)"
                    >
                      {uploadingChecklist
                        ? <><FaSpinner className="text-xs animate-spin" /> Uploading…</>
                        : <><FaFileUpload className="text-xs" /> Upload</>}
                    </button>
                    <button
                      onClick={() => setShowAddChecklistSection(true)}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      <FaPlus className="text-xs" /> Add Section
                    </button>
                    <PageGuide pageKey="checklist" />
                  </div>
                </div>

                {/* Upload / template helper */}
                <p className="text-xs text-neutral-500 -mt-3 mb-4">
                  Import from a spreadsheet — column <span className="font-medium">Item</span> (required), with optional <span className="font-medium">Qty</span> and <span className="font-medium">Unit</span>. Download a template:{' '}
                  <button type="button" onClick={() => handleDownloadChecklistTemplate('csv')} className="text-blue-600 hover:underline font-medium">CSV</button>
                  {' '}·{' '}
                  <button type="button" onClick={() => handleDownloadChecklistTemplate('xlsx')} className="text-blue-600 hover:underline font-medium">Excel</button>
                </p>

                {/* Upload validation errors */}
                {checklistUploadErrors.length > 0 && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 text-red-700 font-semibold">
                        <FaExclamationTriangle className="text-red-500" />
                        Please fix {checklistUploadErrors.length} {checklistUploadErrors.length === 1 ? 'error' : 'errors'} and upload again
                      </div>
                      <button
                        onClick={() => setChecklistUploadErrors([])}
                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                        title="Dismiss"
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <ul className="mt-2 space-y-1 text-sm text-red-600 list-disc list-inside">
                      {checklistUploadErrors.map((err, i) => (
                        <li key={i}>
                          {err.row ? <span className="font-medium">Row {err.row}: </span> : null}
                          {err.message}
                          {err.item ? <span className="text-red-400"> ({err.item})</span> : null}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-xs text-red-500">
                      Columns: <span className="font-medium">Item</span> (required); <span className="font-medium">Qty</span> (a number, optional); <span className="font-medium">Unit</span> (optional). E.g. Vegetable, 5, lbs → "Vegetable (5 lbs)". Leave Qty/Unit blank for a plain item. CSV or Excel (.xlsx).
                    </p>
                  </div>
                )}

                {showAddChecklistSection && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <input
                      type="text"
                      value={newChecklistSectionName}
                      onChange={(e) => setNewChecklistSectionName(e.target.value)}
                      placeholder="Section name..."
                      className="flex-1 px-3 py-2 border border-green-300 rounded-lg text-sm focus:outline-none focus:border-green-500"
                      autoFocus
                      onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistSection()}
                    />
                    <button onClick={handleAddChecklistSection} className="text-green-600 hover:text-green-700 p-2"><FaCheck /></button>
                    <button onClick={() => { setShowAddChecklistSection(false); setNewChecklistSectionName(''); }} className="text-neutral-400 hover:text-neutral-600 p-2"><FaTimes /></button>
                  </div>
                )}

                <div className="space-y-3 mb-8">
                  {checklistSections.map((section, sIdx) => renderChecklistSection(section, sIdx, false))}
                  {(checklistData.customSections || []).map((sectionTitle, sIdx) =>
                    renderChecklistSection({ title: sectionTitle, items: [] }, `custom-${sIdx}`, true)
                  )}
                  {checklistSections.length === 0 && (checklistData.customSections || []).length === 0 && (
                    <div className="text-center py-8">
                      <FaClipboardList className="text-neutral-300 text-4xl mx-auto mb-3" />
                      <p className="text-neutral-500">No checklist items yet. Add a section above or generate a packing list below.</p>
                    </div>
                  )}
                </div>

                {request_id && (
                  <PackingList requestId={request_id} isOwner={isOwner} />
                )}
              </>
            )}

            {/* Other Points of Attraction Tab Content */}
            {activeTab === 'attractions' && status === 'completed' && content && (
              <>
                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-purple-500">
                  <div className="flex items-center gap-3">
                    <FaLandmark className="text-purple-500 text-2xl" />
                    <h2 className="text-2xl font-bold text-neutral-800">
                      Other Points of Attraction
                    </h2>
                  </div>
                  <PageGuide pageKey="attractions" />
                </div>
                {parseContentSections(content).otherPointsContent ? (
                  <AttractionsPanel
                    content={parseContentSections(content).otherPointsContent}
                    itineraryDays={itineraryDays}
                    requestId={request_id}
                    isOwner={isOwner}
                    destination={end_location}
                    onAttractionAdded={(name) => {
                      showSuccess(`"${name}" added to your itinerary!`, { duration: 2000 });
                      setTimeout(() => window.location.reload(), 2000);
                    }}
                    onAttractionError={(name) => {
                      showError(`Failed to add "${name}". Please try again.`);
                    }}
                    userTier={effectiveTier}
                    limitsWaived={limitsWaived}
                    onViewPricing={onViewPricing}
                  />
                ) : (
                  <div className="text-center py-12">
                    <FaLandmark className="text-neutral-300 text-5xl mx-auto mb-4" />
                    <p className="text-neutral-500 text-lg">No additional attractions found in this itinerary.</p>
                    <p className="text-neutral-400 text-sm mt-2">Additional points of interest are automatically extracted when available.</p>
                  </div>
                )}
              </>
            )}

            {/* Flights Tab Content */}
            {activeTab === 'flights' && status === 'completed' && request_id && (() => {
              // Find the first arrival airport IATA code from itinerary content.
              // Scans all lines for "arrive"/"arrival" near an airport with (XXX) code,
              // skipping the departure airport that matches start_location.
              let firstArrivalAirport = end_location;
              if (content) {
                const startLower = (start_location || '').toLowerCase();
                // Collect all IATA codes mentioned with Airport context
                const airportPattern = /\b([A-Z][A-Za-z\s.''-]+Airport[^)]*?)\(([A-Z]{3})\)/gi;
                let match;
                const departCodes = new Set();
                const allAirports = [];
                while ((match = airportPattern.exec(content)) !== null) {
                  const name = match[1].replace(/\*+/g, '').trim().toLowerCase();
                  const code = match[2];
                  // Check surrounding text (100 chars before) for departure context
                  const before = content.substring(Math.max(0, match.index - 100), match.index).toLowerCase();
                  const isDeparture = before.includes('depart') || before.includes('check in for')
                    || name.includes(startLower.split(',')[0].trim().toLowerCase());
                  allAirports.push({ code, isDeparture });
                  if (isDeparture) departCodes.add(code);
                }
                // Also scan for standalone "Arrive ... (XXX)" patterns
                const arrivePattern = /arriv\w*[^(]{0,80}\(([A-Z]{3})\)/gi;
                while ((match = arrivePattern.exec(content)) !== null) {
                  const code = match[1];
                  if (!departCodes.has(code)) {
                    firstArrivalAirport = code;
                    break;
                  }
                }
                // Fallback: first airport code that isn't a departure
                if (firstArrivalAirport === end_location) {
                  const arrival = allAirports.find(a => !a.isDeparture && !departCodes.has(a.code));
                  if (arrival) firstArrivalAirport = arrival.code;
                }
              }
              return (
              <FlightManager
                requestId={request_id}
                isOwner={isOwner}
                startDate={start_date}
                endDate={end_date}
                startLocation={start_location}
                endLocation={firstArrivalAirport}
                onFlightsChange={() => {
                  // Trigger itinerary refresh when flights are added/changed
                  if (onChatModificationApplied) {
                    onChatModificationApplied();
                  }
                }}
              />
              );
            })()}

            {/* Expenses Tab Content — gated on owner's plan */}
            {activeTab === 'expenses' && status === 'completed' && request_id && hasExpenseAccess && (
              <Expenses requestId={request_id} isOwner={isOwner} onExpenseChange={onRefreshItinerary} userTier={effectiveTier} limitsWaived={limitsWaived} onViewPricing={onViewPricing} />
            )}

            {/* Timeline Tab Content - Only available when there are invited members AND trip is active */}
            {activeTab === 'timeline' && status === 'completed' && request_id && invited_members_count > 0 && tripIsActive && (
              <TripTimeline requestId={request_id} itineraryContent={content} structuredItinerary={structured_itinerary} isOwner={isOwner} onViewPricing={onViewPricing} />
            )}

            {/* Rating Tab Content - Available to owners and members */}
            {activeTab === 'rating' && status === 'completed' && request_id && (
              <RatingFeedback requestId={request_id} tripStartDate={start_date} tripEndDate={end_date} />
            )}
          </div>
        </div>
      )}

      {/* AI Chatbot - Solo plan only (individual tier owners) */}
      {(status === 'completed' || status === 'processing') && request_id && isOwner && userTier === 'individual' && (
        <Chatbot requestId={request_id} onModificationApplied={handleModificationApplied} />
      )}

      {/* Group Chat - Group plan and above, OR members of shared itineraries */}
      {(status === 'completed' || status === 'processing') && request_id && (hasInviteAccess || isMember) && (
        <GroupChat requestId={request_id} isOwner={isOwner} onModificationApplied={handleModificationApplied} />
      )}

      {/* Hidden PDF Content - Styled for PDF Export */}
      {status === 'completed' && content && (
        <div
          ref={pdfContentRef}
          className="pdf-content-container"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '0',
            width: '210mm',
            minHeight: '297mm',
            padding: '20px',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontSize: '11pt',
            lineHeight: '1.5',
            color: '#1f2937',
            backgroundColor: '#ffffff'
          }}
        >
          {/* PDF Header */}
          <div style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 50%, #f97316 100%)',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '20px',
            color: 'white'
          }}>
            <h1 style={{ fontSize: '24pt', fontWeight: 'bold', margin: '0 0 8px 0' }}>
              Your Travel Itinerary
            </h1>
            <p style={{ fontSize: '12pt', margin: 0, opacity: 0.9 }}>
              {start_location} → {end_location}
            </p>
            {start_date && (
              <p style={{ fontSize: '11pt', margin: '8px 0 0 0', opacity: 0.9 }}>
                {start_date}{end_date ? ` to ${end_date}` : ''}
              </p>
            )}
          </div>

          {/* PDF Trip Summary */}
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontSize: '14pt',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: '0 0 16px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid #0ea5e9'
            }}>
              Trip Summary
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* From */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                <span style={{ color: '#0ea5e9', fontSize: '16pt' }}>📍</span>
                <div>
                  <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>From</p>
                  <p style={{ fontWeight: '600', margin: 0 }}>{start_location}</p>
                </div>
              </div>

              {/* To */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                <span style={{ color: '#06b6d4', fontSize: '16pt' }}>✈️</span>
                <div>
                  <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>To</p>
                  <p style={{ fontWeight: '600', margin: 0 }}>{end_location}</p>
                </div>
              </div>

              {/* Interests */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                <span style={{ color: '#ec4899', fontSize: '16pt' }}>❤️</span>
                <div>
                  <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>Interests</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {interests.map((interest, idx) => (
                      <span
                        key={idx}
                        style={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '9pt',
                          fontWeight: '500'
                        }}
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Food Preference */}
              {food_preference && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                  <span style={{ color: '#f97316', fontSize: '16pt' }}>🍽️</span>
                  <div>
                    <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>Food Preference</p>
                    <p style={{ fontWeight: '600', margin: 0 }}>{food_preference}</p>
                  </div>
                </div>
              )}

              {/* Travel Dates */}
              {start_date && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                  <span style={{ color: '#f97316', fontSize: '16pt' }}>📅</span>
                  <div>
                    <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>Travel Dates</p>
                    <p style={{ fontWeight: '600', margin: 0 }}>{start_date}{end_date ? ` to ${end_date}` : ''}</p>
                  </div>
                </div>
              )}

              {/* Transportation */}
              {transportation_mode && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }} className="pdf-no-break">
                  <span style={{ color: '#06b6d4', fontSize: '16pt' }}>🚗</span>
                  <div>
                    <p style={{ fontSize: '9pt', color: '#64748b', textTransform: 'uppercase', margin: '0 0 4px 0' }}>Transportation</p>
                    <p style={{ fontWeight: '600', margin: 0 }}>
                      {transportation_mode}
                      {transportation_mode === 'Driving' && (avoid_toll_roads || prefer_toll_roads || scenic_roads) && (
                        <span style={{ display: 'block', fontSize: '10pt', color: '#64748b', marginTop: '4px' }}>
                          {[
                            avoid_toll_roads && 'Avoid tolls',
                            prefer_toll_roads && 'Prefer tolls',
                            scenic_roads && 'Scenic routes'
                          ].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PDF Itinerary Content */}
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontSize: '14pt',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: '0 0 16px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid #0ea5e9'
            }}>
              Your Personalized Itinerary
            </h2>
            <PDFFormattedContent content={parseContentSections(content).mainContent} />
          </div>

          {/* PDF Checklist (if exists) */}
          {parseContentSections(content).checklistContent && (
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '14pt',
                fontWeight: 'bold',
                color: '#166534',
                margin: '0 0 16px 0',
                paddingBottom: '12px',
                borderBottom: '2px solid #22c55e'
              }}>
                ✅ Travel Checklist
              </h2>
              <PDFFormattedContent content={parseContentSections(content).checklistContent} isChecklist={true} />
            </div>
          )}

          {/* PDF Other Attractions (if exists) */}
          {parseContentSections(content).otherPointsContent && (
            <div style={{
              backgroundColor: '#faf5ff',
              border: '1px solid #e9d5ff',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '14pt',
                fontWeight: 'bold',
                color: '#7c3aed',
                margin: '0 0 16px 0',
                paddingBottom: '12px',
                borderBottom: '2px solid #a855f7'
              }}>
                🏛️ Other Points of Attraction
              </h2>
              <PDFFormattedContent content={parseContentSections(content).otherPointsContent} />
            </div>
          )}

          {/* PDF Footer */}
          <div style={{
            textAlign: 'center',
            padding: '16px',
            borderTop: '1px solid #e2e8f0',
            color: '#64748b',
            fontSize: '9pt'
          }}>
            <p style={{ margin: 0 }}>Generated by ItinerarySeek.com • Your AI-Powered Travel Companion</p>
          </div>
        </div>
      )}

      {/* Rating Prompt Modal */}
      <RatingPromptModal
        isOpen={showRatingPrompt}
        onClose={() => setShowRatingPrompt(false)}
        requestId={request_id}
        tripName={`${start_location} → ${end_location}`}
        tripStartDate={start_date}
        tripEndDate={end_date}
        onRatingSubmitted={() => {
          setHasUserRated(true);
          setShowRatingPrompt(false);
        }}
      />

      {/* Share PDF Modal */}
      {showSharePdfModal && (
        <SharePdfModal
          requestId={request_id}
          itineraryTitle={start_location === end_location ? end_location : `${start_location} to ${end_location}`}
          onClose={() => setShowSharePdfModal(false)}
        />
      )}

      {/* Re-optimize Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReoptimizeConfirm}
        onClose={() => setShowReoptimizeConfirm(false)}
        onConfirm={() => {
          setShowReoptimizeConfirm(false);
          handleReoptimize();
        }}
        title="Re-optimize Itinerary?"
        message={`This will use AI to generate ${Object.values(attractionStatuses).filter(s => s.visited).length} new attraction recommendation(s) to replace the places you've marked as visited. Your original itinerary will be preserved, and new suggestions will be added. The page will reload after regeneration. Do you want to continue?`}
        confirmText="Yes, Re-optimize"
        cancelText="Cancel"
        type="warning"
      />

      {/* PDF Generation Loading Overlay */}
      {isGeneratingPDF && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-elevated max-w-md mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <FaFilePdf className="text-6xl text-primary-600 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-neutral-800 mb-2">Generating PDF</h3>
                <p className="text-neutral-600">
                  Preparing your itinerary with all directions...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Translation Loading Overlay */}
      {isTranslating && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-elevated max-w-md mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <FaGlobe className="text-6xl text-green-600 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-neutral-800 mb-2">Translating Itinerary</h3>
                <p className="text-neutral-600">
                  AI is translating your itinerary to the selected language...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusIndicator = ({ status }) => {
  const statusConfig = {
    pending: {
      label: 'Pending',
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: FaSpinner,
      animate: true
    },
    processing: {
      label: 'Processing',
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      icon: FaSpinner,
      animate: true
    },
    completed: {
      label: 'Completed',
      bg: 'bg-green-100',
      text: 'text-green-700',
      icon: FaCheckCircle
    },
    failed: {
      label: 'Failed',
      bg: 'bg-red-100',
      text: 'text-red-700',
      icon: FaExclamationTriangle
    },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className={`${config.bg} ${config.text} px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-3 mx-auto max-w-xs`}>
      <Icon className={`text-xl ${config.animate ? 'animate-spin' : ''}`} />
      <span>{config.label}</span>
    </div>
  );
};

// Helper function to check if a checklist line is a packing-related item
const isPackingRelatedItem = (line) => {
  const trimmed = line.trim().toLowerCase();

  // Skip empty lines, headers, or section titles (keep them)
  if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('**')) {
    return false;
  }

  // Packing-related keywords and patterns
  const packingPatterns = [
    // Clothing items
    /\b(shirt|pants|shorts|jacket|coat|sweater|hoodie|dress|skirt|jeans|underwear|socks|shoes|boots|sandals|sneakers|hat|cap|scarf|gloves|swimsuit|swimwear|bikini|trunks|pajamas|sleepwear|clothing|clothes|outfit|attire|layers|wardrobe)\b/i,
    // Toiletries
    /\b(toothbrush|toothpaste|shampoo|conditioner|soap|deodorant|razor|shaving|lotion|sunscreen|sunblock|moisturizer|makeup|cosmetics|toiletries|hygiene|skincare|hairbrush|comb|floss|mouthwash)\b/i,
    // Electronics (non-task items)
    /\b(charger|adapter|power bank|headphones|earbuds|camera|laptop|tablet|kindle|e-reader|cables|usb|hdmi|plug adapter|voltage converter)\b/i,
    // Documents (when mentioned as items to pack, not tasks)
    /\b(pack.*(passport|visa|id|license|documents|tickets|itinerary)|bring.*(passport|visa|id|license|documents))\b/i,
    // Health/Medical supplies
    /\b(medication|medicine|prescription|vitamins|first aid|band-aids|bandages|pain reliever|allergy|antihistamine|sunburn|insect repellent|bug spray|hand sanitizer)\b/i,
    // Gear and accessories
    /\b(luggage|suitcase|backpack|daypack|travel pillow|eye mask|earplugs|umbrella|raincoat|poncho|water bottle|snacks|travel-size|ziplock|packing cubes|laundry bag|toiletry bag)\b/i,
    // Generic packing phrases
    /\b(pack (your|the|light|extra)|bring (extra|spare)|don't forget to pack|remember to bring|essential items|travel essentials|packing list)\b/i,
    // Miscellaneous travel items
    /\b(towel|blanket|pillow|flashlight|batteries|binoculars|guidebook|phrasebook|travel journal|notebook|pen|pencil)\b/i
  ];

  // Check if line matches any packing pattern
  return packingPatterns.some(pattern => pattern.test(trimmed));
};

// Helper function to filter out packing items from checklist content
const filterPackingFromChecklist = (checklistLines) => {
  return checklistLines.filter(line => {
    const trimmed = line.trim();
    // Keep empty lines and headers
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('**')) {
      return true;
    }
    // Filter out packing-related items
    return !isPackingRelatedItem(line);
  });
};

// Helper function to check if a line is a driving route section header
const isDrivingRouteSectionHeader = (line) => {
  const trimmed = line.trim();
  return (
    // Match "# Driving Journey:", "## Driving Journey", etc.
    /^#{1,3}\s*(driving\s+journey|driving\s+route|road\s+trip\s+route|route\s+to|getting\s+there)/i.test(trimmed) ||
    // Match "Recommended Driving Route", "Driving Directions", etc.
    /^(##\s*)?(recommended\s+)?(driving|road)\s+(route|directions?|journey)/i.test(trimmed) ||
    /^\*\*\s*(recommended\s+)?(driving|road)\s+(route|directions?|journey)\s*\*\*/i.test(trimmed) ||
    // Match route-related headers
    /^(##\s*)?(route\s+overview|route\s+description|driving\s+instructions)/i.test(trimmed) ||
    /^(##\s*)?(how\s+to\s+get\s+there|getting\s+there\s+by\s+(car|driving))/i.test(trimmed) ||
    // Match "Recommended Route:" header
    /^(##\s*)?\*?\*?recommended\s+route:?\*?\*?/i.test(trimmed)
  );
};

// Helper function to check if a line is detailed driving instruction content
const isDrivingInstructionContent = (line) => {
  const trimmed = line.trim();
  // Match typical driving instruction patterns
  return (
    // Highway/Interstate instructions
    /\b(take|merge onto|exit|continue on|follow)\s+(the\s+)?(I-\d+|US-\d+|Route\s+\d+|Highway\s+\d+|SR-\d+|MD-\d+)/i.test(trimmed) ||
    // Turn-by-turn instructions
    /\b(turn\s+(left|right)|keep\s+(left|right)|bear\s+(left|right)|make\s+a\s+(left|right|u-turn))\b/i.test(trimmed) ||
    // Mile markers and exits
    /\b(in\s+\d+(\.\d+)?\s+(miles?|km)|exit\s+\d+\b|after\s+\d+(\.\d+)?\s+(miles?|km))\b/i.test(trimmed) ||
    // Specific route descriptions
    /\b(head\s+(north|south|east|west)|merge\s+onto|ramp\s+to|toward\s+downtown)\b/i.test(trimmed) ||
    // Route description labels
    /^(route\s+description|approximate\s+travel\s+time|best\s+time\s+to\s+depart|recommended\s+route):/i.test(trimmed) ||
    // Numbered driving steps (1. From..., 2. Merge onto...)
    /^\d+\s*(\.|\))\s*(from|take|merge|continue|follow|near|head)/i.test(trimmed) ||
    // Parkway/highway names
    /\b(parkway|expressway|turnpike|interstate|freeway|beltway)\b/i.test(trimmed) ||
    // Follow signs instructions
    /\bfollow\s+signs\s+(towards?|to|for)\b/i.test(trimmed)
  );
};

// Helper function to filter out driving route sections from content
const filterDrivingRouteSections = (lines) => {
  let inDrivingSection = false;
  let result = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if we're entering a driving route section
    if (isDrivingRouteSectionHeader(line)) {
      inDrivingSection = true;
      continue; // Skip the header
    }

    // Check if we're exiting the driving section (new major section)
    if (inDrivingSection) {
      // Exit driving section on new Day header
      if (trimmed.match(/^(#{1,3}\s*)?day\s+\d+/i) ||
          trimmed.match(/^\*\*\s*day\s+\d+/i)) {
        inDrivingSection = false;
        result.push(line);
        continue;
      }

      // Exit on other major section headers (but not driving-related ones)
      if ((trimmed.startsWith('#') || (trimmed.startsWith('**') && trimmed.endsWith('**'))) &&
          !isDrivingRouteSectionHeader(line) &&
          !isDrivingInstructionContent(line)) {
        // Check if this looks like a new content section (not part of driving)
        if (/^(#{1,3}\s*)?(morning|afternoon|evening|lunch|dinner|breakfast|attractions?|accommodations?|where\s+to\s+stay|checklist|other\s+points)/i.test(trimmed) ||
            /^\*\*\s*(morning|afternoon|evening|lunch|dinner|breakfast)/i.test(trimmed)) {
          inDrivingSection = false;
          result.push(line);
          continue;
        }
      }

      // Skip everything in the driving section
      continue;
    }

    // Not in a driving section - but still check for standalone driving instructions
    // that might appear outside a dedicated section
    if (isDrivingInstructionContent(line) &&
        // Only filter if it looks like a standalone driving instruction, not travel time mentions
        !/^\s*•?\s*travel:?\s*\d+\s*min/i.test(trimmed)) {
      // Check if this is numbered step (like "1. Take I-95...")
      if (/^\d+\s*(\.|\))/.test(trimmed)) {
        continue; // Skip numbered driving steps
      }
    }

    result.push(line);
  }

  return result;
};

// Parse checklist content into structured sections with items
const parseChecklistIntoSections = (checklistContent) => {
  if (!checklistContent) return [];
  const lines = checklistContent.split('\n');
  const sections = [];
  let currentSection = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Detect section headers: ## Header, ### Header, **Header**
    const hashMatch = trimmed.match(/^#{2,3}\s+(.+)/);
    const boldMatch = !hashMatch && trimmed.match(/^\*\*\s*(.+?)\s*\*\*\s*:?\s*$/);
    const headerMatch = hashMatch || boldMatch;

    if (headerMatch) {
      const title = headerMatch[1].replace(/\*\*/g, '').replace(/:$/, '').trim();
      if (/^(checklist|travel checklist|packing checklist|pre-trip checklist)$/i.test(title)) continue;
      currentSection = { title, items: [] };
      sections.push(currentSection);
      continue;
    }

    // Detect list items
    const bulletMatch = trimmed.match(/^\s*[-•*]\s+(\[[\sxX]?\]\s*)?(.+)/);
    const numberedMatch = !bulletMatch && trimmed.match(/^\d+[.)]\s+(.+)/);
    const itemMatch = bulletMatch || numberedMatch;

    if (itemMatch) {
      const text = (bulletMatch ? itemMatch[2] : itemMatch[1]).trim();
      if (text) {
        if (!currentSection) {
          currentSection = { title: 'General', items: [] };
          sections.push(currentSection);
        }
        currentSection.items.push(text);
      }
    }
  }

  return sections;
};

// Helper function to parse content and extract sections
const parseContentSections = (content) => {
  if (!content) return { mainContent: '', checklistContent: '', otherPointsContent: '' };

  const lines = content.split('\n');
  let mainContent = [];
  let checklistContent = [];
  let otherPointsContent = [];

  let currentSection = 'main'; // 'main', 'checklist', or 'otherPoints'

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim().toLowerCase();

    // Detect Checklist section header
    if (trimmedLine.match(/^(##\s*)?(checklist|travel checklist|packing checklist|pre-trip checklist)/i) ||
        trimmedLine.match(/^\*\*\s*(checklist|travel checklist|packing checklist|pre-trip checklist)\s*\*\*/i)) {
      currentSection = 'checklist';
      checklistContent.push(line);
      continue;
    }

    // Detect Other Points of Attraction section header
    if (trimmedLine.match(/^(##\s*)?(other points of attraction|other points of interest|other attractions|points of interest|additional attractions|nearby attractions)/i) ||
        trimmedLine.match(/^\*\*\s*(other points of attraction|other points of interest|other attractions|points of interest|additional attractions|nearby attractions)\s*\*\*/i)) {
      currentSection = 'otherPoints';
      otherPointsContent.push(line);
      continue;
    }

    // Detect if we're starting a new major section (which would end checklist/otherPoints)
    if ((currentSection === 'checklist' || currentSection === 'otherPoints') &&
        (trimmedLine.match(/^(#{1,3}\s*)?day\s+\d+/i) ||
         trimmedLine.match(/^\*\*\s*day\s+\d+/i) ||
         trimmedLine.match(/^(##\s*)?(estimated|budget|cost|expense|transportation|route|summary|conclusion|final)/i) ||
         trimmedLine.match(/^\*\*\s*(estimated|budget|cost|expense|transportation|route|summary|conclusion|final)/i))) {
      currentSection = 'main';
    }

    // Add line to appropriate section
    if (currentSection === 'checklist') {
      checklistContent.push(line);
    } else if (currentSection === 'otherPoints') {
      otherPointsContent.push(line);
    } else {
      mainContent.push(line);
    }
  }

  // Filter packing items from checklist content
  const filteredChecklist = filterPackingFromChecklist(checklistContent);

  // Filter out driving route sections from main content
  const filteredMainContent = filterDrivingRouteSections(mainContent);

  return {
    mainContent: filteredMainContent.join('\n'),
    checklistContent: filteredChecklist.join('\n'),
    otherPointsContent: otherPointsContent.join('\n')
  };
};

// OSRM region bounding boxes — mirrors backend directions_service.py OSRM_REGIONS
const SUPPORTED_REGIONS = [
  { name: 'US-West', minLat: 31.0, maxLat: 49.5, minLng: -125.0, maxLng: -102.0 },
  { name: 'US-Midwest', minLat: 36.0, maxLat: 49.5, minLng: -104.0, maxLng: -80.0 },
  { name: 'US-Northeast', minLat: 38.0, maxLat: 47.5, minLng: -80.0, maxLng: -66.0 },
  { name: 'US-South', minLat: 24.0, maxLat: 40.0, minLng: -106.0, maxLng: -75.0 },
  { name: 'Canada', minLat: 41.0, maxLat: 84.0, minLng: -141.0, maxLng: -52.0 },
  { name: 'Australia-Oceania', minLat: -50.0, maxLat: 0.0, minLng: 100.0, maxLng: 180.0 },
  { name: 'Iceland', minLat: 63.0, maxLat: 67.0, minLng: -25.0, maxLng: -13.0 },
];

// Check if a coordinate falls within any supported OSRM region
const isCoordinateInSupportedRegion = (lat, lng) => {
  return SUPPORTED_REGIONS.some(region =>
    lat >= region.minLat && lat <= region.maxLat &&
    lng >= region.minLng && lng <= region.maxLng
  );
};

const FormattedContent = ({
  content,
  showCheckboxes = true,
  showDirections = false,
  isOwner = false,
  destination = '',
  requestId = null,
  cachedDirections = null,
  onDirectionsCached = null,
  isInDaySection = false,
  expandAllDirections = false,
  weatherData = null,
  weatherLoading = false,
  flights = [],
  dayNumber = null,
  tripStartDate = null,
  attractionStatuses = {},
  onAttractionStatusUpdate = null,
  showAttractionControls = false,
  onExplore = null,
  checkedItems = {},
  onToggleCheck = null
}) => {
  // Extract location name and coordinates from a line
  const extractLocationInfo = (text) => {
    // Match patterns like "**Location Name** Coordinates: (lat, lng)" or "**Location Name** (lat, lng)"
    // Also handles "Coordinates: N/A" for places without coordinates
    const coordMatchNew = text.match(/\*\*([^*]+)\*\*\s*Coordinates:\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/);
    if (coordMatchNew) {
      return {
        name: coordMatchNew[1].trim(),
        lat: parseFloat(coordMatchNew[2]),
        lng: parseFloat(coordMatchNew[3])
      };
    }

    // Legacy format without "Coordinates:" prefix
    const coordMatch = text.match(/\*\*([^*]+)\*\*\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/);
    if (coordMatch) {
      return {
        name: coordMatch[1].trim(),
        lat: parseFloat(coordMatch[2]),
        lng: parseFloat(coordMatch[3])
      };
    }

    // Format with rating before coordinates: Name (X.X/5) (lat, lng)
    // First, check if line contains rating pattern and coordinates
    if (text.includes('/5)') && text.match(/\(-?\d+\.\d+,\s*-?\d+\.\d+\)/)) {
      // Extract coordinates first (they're at the end)
      const coordMatch = text.match(/\((-?\d+\.\d+),\s*(-?\d+\.\d+)\)\s*$/);
      // Extract rating
      const ratingMatch = text.match(/\((\d+(?:\.\d+)?)\s*\/\s*5\)/);

      if (coordMatch && ratingMatch) {
        // Name is everything before the rating
        const ratingIndex = text.indexOf(ratingMatch[0]);
        const name = text.substring(0, ratingIndex).trim();

        return {
          name: name,
          lat: parseFloat(coordMatch[1]),
          lng: parseFloat(coordMatch[2]),
          starRating: Math.round(parseFloat(ratingMatch[1]))
        };
      }
    }

    // Simple coordinate format without rating: "Name (lat, lng)"
    // Must start with uppercase letter and have coordinates
    const simpleCoordMatch = text.match(/^([A-Z][^(]*[^(\s])\s*\((-?\d+\.\d+),\s*(-?\d+\.\d+)\)/);
    if (simpleCoordMatch && !text.includes('/5)')) {
      return {
        name: simpleCoordMatch[1].trim(),
        lat: parseFloat(simpleCoordMatch[2]),
        lng: parseFloat(simpleCoordMatch[3])
      };
    }

    // Match just bold location names (likely places)
    const boldMatch = text.match(/\*\*([^*]+)\*\*/);
    if (boldMatch) {
      const name = boldMatch[1].trim();

      // Filter out section headers - only filter if:
      // 1. Name ends with a colon (e.g., "Morning:", "Lunch:", "Dates:")
      // 2. Name is an exact match to common section headers
      const hasColon = name.endsWith(':');
      const exactSectionHeaders = /^(morning|afternoon|evening|night|lunch|breakfast|dinner|snack|tip|tips|note|notes|budget|total|checklist|summary|dates?|getting there|getting around|accommodation|lodging|where to stay|transportation|travel time|duration|distance|weather|temperature|climate|permits?|fees?|entrance fee|tickets?|currency|language|visa|insurance|emergency|safety|practical information|important notes?|trip overview|itinerary overview)$/i;

      // If it has a colon or is an exact match to section header, filter it out
      if (hasColon || exactSectionHeaders.test(name)) {
        return null;
      }

      // Otherwise, it's a valid location
      return { name, lat: null, lng: null };
    }

    return null;
  };

  // Pre-extract all locations with coordinates for explore buttons
  const locationsWithCoords = React.useMemo(() => {
    if (!content || !onExplore) return [];
    const lines = content.split('\n');
    const locations = [];
    lines.forEach((line, index) => {
      const locInfo = extractLocationInfo(line);
      if (locInfo && locInfo.lat && locInfo.lng) {
        locations.push({ ...locInfo, lineIndex: index });
      }
    });
    return locations;
  }, [content, onExplore]);

  // Find the next location with coordinates after a given location
  const getNextLocationWithCoords = (currentLocation) => {
    if (!currentLocation || !currentLocation.lat || !currentLocation.lng) return null;
    // Use coordinate-based matching (more reliable than name matching)
    const currentIdx = locationsWithCoords.findIndex(
      loc => Math.abs(loc.lat - currentLocation.lat) < 0.0001 && Math.abs(loc.lng - currentLocation.lng) < 0.0001
    );
    if (currentIdx >= 0 && currentIdx < locationsWithCoords.length - 1) {
      return locationsWithCoords[currentIdx + 1];
    }
    return null;
  };

  // Get attraction key for status lookup
  const getAttractionKey = (locationInfo) => {
    if (locationInfo.lat && locationInfo.lng) {
      return `${locationInfo.name.substring(0, 50)}_${locationInfo.lat.toFixed(4)}_${locationInfo.lng.toFixed(4)}`;
    }
    return `${locationInfo.name.substring(0, 50)}_${dayNumber || 0}`;
  };

  // Render location with actions
  // Pattern to detect accommodations - expanded to catch more types
  const ACCOMMODATION_PATTERN = /\bhotel\b|\bresort\b|\bmotel\b|\bhostel\b|\bairbnb\b|\bb&b\b|\bbed\s*&?\s*breakfast\b|\bguesthouse\b|\bguest\s*house\b|\bglamping\b|\bvilla\b|\bchalet\b|\bresidence\b|\bresidency\b|\bpenthouse\b|\baccomodation\b|\baccommodation\b|\briad\b|\bryokan\b|\bpension\b|\bposada\b|\bpousada\b|\bhacienda\b|\bmanor\b|\bchateau\b|\bcastle\s*hotel\b/i;

  const renderLocationWithActions = (locationInfo, formattedHtml, key, lineText = '', lineIndex = -1) => {
    // Only show review links if we're in a day section (day-by-day itinerary)
    const showReviewLinks = isInDaySection;

    // Get status for this attraction
    const attractionKey = getAttractionKey(locationInfo);
    const status = attractionStatuses[attractionKey] || {};
    const isVisited = status.visited || false;
    const isLocked = status.locked || false;
    const customTime = status.custom_time || '';

    // Check if there's a next location for explore button
    const nextLoc = getNextLocationWithCoords(locationInfo);
    const showExploreButton = onExplore && locationInfo.lat && locationInfo.lng && nextLoc;

    // Use AttractionCard for locations with coordinates in day sections
    if (isInDaySection && locationInfo.lat && locationInfo.lng) {
      // Extract description, booking link, and star rating
      let description = '';
      let bookingLink = null;
      // Use star rating from locationInfo if already extracted (e.g., from rating format)
      let starRating = locationInfo.starRating || null;

      // Check if this is an accommodation by name pattern
      let isAccommodation = ACCOMMODATION_PATTERN.test(locationInfo.name);

      // Also check if this location is within a "Where to Stay" section by looking at previous lines
      const lines = content ? content.split('\n') : [];
      if (!isAccommodation && lineIndex >= 0) {
        for (let i = lineIndex - 1; i >= Math.max(0, lineIndex - 4); i--) {
          const prevLine = lines[i]?.trim() || '';
          // Found "Where to Stay" header - this location is an accommodation
          if (prevLine.match(/where\s*to\s*stay/i) || prevLine.match(/🏨/) || prevLine.match(/accommodation.*:/i)) {
            isAccommodation = true;
            break;
          }
          // Stop looking back if we hit another section
          if (prevLine.match(/^(Morning|Afternoon|Evening|Night)\s*:/i) || prevLine.match(/^Day\s+\d+/i)) {
            break;
          }
        }
      }

      // Also check the current line for star rating indicators (⭐ or X-star)
      if (!isAccommodation && lineText) {
        if (lineText.match(/⭐|★|\d\s*-?\s*star/i)) {
          isAccommodation = true;
        }
      }

      // Combine current line and next few lines to find booking link and description
      let combinedText = lineText || '';

      // Look at following lines to find description and other info for ALL locations
      // For accommodations, look at more lines (up to 8), for others look at up to 5
      const maxLines = isAccommodation ? 8 : 5;
      if (lineIndex >= 0 && lines.length > lineIndex) {
        for (let i = lineIndex + 1; i < Math.min(lineIndex + maxLines, lines.length); i++) {
          const nextLine = lines[i]?.trim() || '';
          // Stop if we hit another location with coordinates or section header
          if (/^\*\*[^*]+\*\*.*Coordinates:/i.test(nextLine)) break;
          if (/^\*\*[^*]+\*\*.*\((-?\d+\.\d+),\s*(-?\d+\.\d+)\)/.test(nextLine)) break;
          // Stop if we hit rating format location: Name (X.X/5) (lat, lng)
          if (/^[A-Z][^(]*\(\d(?:\.\d)?\s*\/\s*5\)\s*\((-?\d+\.\d+),\s*(-?\d+\.\d+)\)/.test(nextLine)) break;
          if (/^#{1,3}\s/.test(nextLine) || /^(?:Day|Part)\s+\d+/i.test(nextLine)) break;
          if (/^(Morning|Afternoon|Evening|Night)\s*:/i.test(nextLine)) break;
          if (/^\d+\.\s+\*\*/.test(nextLine)) break; // Numbered location item
          // Skip empty lines
          if (!nextLine) continue;
          // Skip "Reviews" lines
          if (/^Reviews?$/i.test(nextLine)) continue;
          // Skip "Travel:" lines
          if (/^Travel:/i.test(nextLine)) continue;
          combinedText += ' ' + nextLine;
        }
      }

      // Extract booking link - try multiple patterns
      // Pattern 1: [Book...](url) or [Reserve...](url)
      const bookingMatch = combinedText.match(/\[(Book|Reserve|Check Availability|View)[^\]]*\]\(([^)]+)\)/i);
      if (bookingMatch) bookingLink = bookingMatch[2];

      // Pattern 2: Plain booking.com URL
      if (!bookingLink) {
        const urlMatch = combinedText.match(/(https?:\/\/(?:www\.)?booking\.com[^\s\)\]"'<>]*)/i);
        if (urlMatch) bookingLink = urlMatch[1];
      }

      // Pattern 3: Any hotel booking platform URL
      if (!bookingLink) {
        const hotelUrlMatch = combinedText.match(/(https?:\/\/(?:www\.)?(?:hotels\.com|expedia\.com|agoda\.com|trivago\.com|kayak\.com|tripadvisor\.com\/Hotel)[^\s\)\]"'<>]*)/i);
        if (hotelUrlMatch) bookingLink = hotelUrlMatch[1];
      }

      // Pattern 4: Generic markdown link containing booking-related words
      if (!bookingLink) {
        const genericBookingMatch = combinedText.match(/\[[^\]]*(?:book|reserv|hotel|stay)[^\]]*\]\(([^)]+)\)/i);
        if (genericBookingMatch) bookingLink = genericBookingMatch[1];
      }

      // Extract star rating - try multiple patterns
      const starMatch = combinedText.match(/(\d)\s*[-–]?\s*star/i);
      if (starMatch) starRating = parseInt(starMatch[1]);
      if (!starRating) {
        // Try "X stars" pattern
        const starsMatch = combinedText.match(/(\d)\s*stars/i);
        if (starsMatch) starRating = parseInt(starsMatch[1]);
      }
      if (!starRating) {
        // Count star emojis
        const starEmojis = (combinedText.match(/⭐|★|🌟/g) || []).length;
        if (starEmojis > 0) starRating = Math.min(starEmojis, 5);
      }
      if (!starRating) {
        // Try "rated X" pattern
        const ratedMatch = combinedText.match(/rated\s*(\d)/i);
        if (ratedMatch) starRating = parseInt(ratedMatch[1]);
      }

      // Clean the text to get description
      const cleanedText = combinedText
        .replace(/\*\*[^*]+\*\*/g, '') // Remove bold text
        .replace(/Coordinates:\s*\([^)]+\)/g, '') // Remove coordinates
        .replace(/\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/g, '') // Remove coordinate pattern
        .replace(/\[Book[^\]]*\]\([^)]+\)/g, '') // Remove booking links
        .replace(/\[More[^\]]*\]\([^)]+\)/g, '') // Remove more info links
        .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
        .replace(/⭐+|★+/g, '') // Remove star emojis
        .replace(/\d+-star/gi, '') // Remove X-star text
        .replace(/^[\s\-\*\d.•]+/, '') // Remove leading bullets/numbers
        .replace(/\|/g, '') // Remove pipe characters
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      if (cleanedText && cleanedText.length > 10) {
        description = cleanedText;
      }

      // Clean formattedHtml to remove "More Info" links and star ratings (they're shown separately)
      const cleanedFormattedHtml = formattedHtml
        .replace(/<a[^>]*>\s*More\s*Info\s*<\/a>/gi, '') // Remove "More Info" anchor tags
        .replace(/<a[^>]*>\s*\[More\s*Info\]\s*<\/a>/gi, '') // Remove "[More Info]" anchor tags
        .replace(/\[More\s*Info\]/gi, '') // Remove plain [More Info] text
        .replace(/More\s*Info/gi, '') // Remove plain "More Info" text
        .replace(/⭐+|★+|🌟+/g, '') // Remove star emojis (ratings shown separately)
        .replace(/\d+\s*-?\s*star(s)?/gi, '') // Remove "X-star" or "X stars" text
        .replace(/\s*\|\s*$/, '') // Remove trailing pipe separator
        .replace(/^\s*\|\s*/, '') // Remove leading pipe separator
        .replace(/\s*\|\s*\|/g, ' | ') // Clean up double pipes
        .replace(/\s{2,}/g, ' ') // Clean up multiple spaces
        .trim();

      // Compute check-in/check-out dates for hotel booking. Deliberately not
      // gated on bookingLink: the card renders the button on isAccommodation
      // alone, so requiring a link here produced a visible button that opened a
      // dateless search — which lands on Booking.com with no availability and
      // no prices.
      let checkInDate = null;
      let checkOutDate = null;
      if (isAccommodation && tripStartDate && dayNumber) {
        const startDate = new Date(tripStartDate);
        if (!isNaN(startDate.getTime())) {
          const checkIn = new Date(startDate);
          checkIn.setDate(checkIn.getDate() + dayNumber - 1);
          const checkOut = new Date(checkIn);
          checkOut.setDate(checkOut.getDate() + 1);
          checkInDate = checkIn.toISOString().split('T')[0];
          checkOutDate = checkOut.toISOString().split('T')[0];
        }
      }

      return (
        <AttractionCard
          key={key}
          locationInfo={locationInfo}
          formattedHtml={cleanedFormattedHtml}
          description={description}
          destination={destination}
          isVisited={isVisited}
          isLocked={isLocked}
          customTime={customTime}
          isOwner={isOwner}
          showAttractionControls={showAttractionControls}
          showReviewLinks={showReviewLinks}
          onAttractionStatusUpdate={onAttractionStatusUpdate}
          dayNumber={dayNumber}
          showExploreButton={showExploreButton}
          onExplore={onExplore}
          nextLoc={nextLoc}
          bookingLink={bookingLink}
          starRating={starRating}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          // The parser's verdict, which also promotes a stop on a booking link
          // or a star rating. The card used to re-derive this from the name
          // alone and so dropped every stay the AI didn't literally call a hotel.
          isAccommodation={isAccommodation}
          bookingSurface={requestId ? `itin_${requestId}` : undefined}
        />
      );
    }

    // Fallback to inline rendering for non-day sections or locations without coordinates
    return (
      <div key={key} className={showExploreButton ? "flex flex-col gap-1 w-full" : "inline"}>
        <span className="inline-flex items-start gap-2">
          {/* Visited checkbox - only show for locations WITH coordinates */}
          {/* Anyone can mark as visited, only owner can unmark */}
          {showAttractionControls && onAttractionStatusUpdate && locationInfo.lat && locationInfo.lng && (
            <button
              onClick={() => {
                // Anyone can mark as visited
                // Only owner can unmark (set visited to false)
                if (!isVisited || isOwner) {
                  onAttractionStatusUpdate(
                    dayNumber,
                    locationInfo.name,
                    { lat: locationInfo.lat, lng: locationInfo.lng },
                    { visited: !isVisited }
                  );
                }
              }}
              disabled={isVisited && !isOwner}
              className={`flex-shrink-0 p-1 rounded transition-colors ${
                isVisited
                  ? isOwner
                    ? 'text-green-600 hover:text-green-700 cursor-pointer'
                    : 'text-green-400 opacity-50 cursor-not-allowed'
                  : 'text-neutral-400 hover:text-neutral-600 cursor-pointer'
              }`}
              title={
                isVisited
                  ? isOwner
                    ? 'Click to unmark as visited'
                    : 'Visited (only owner can unmark)'
                  : 'Mark as visited'
              }
            >
              {isVisited ? <FaCheckSquare className="text-lg" /> : <FaRegSquare className="text-lg" />}
            </button>
          )}

          {/* Location name with optional strikethrough if visited */}
          <span className={`inline ${isVisited ? 'opacity-60' : ''}`}>
            <span
              className={isVisited ? 'line-through' : ''}
              dangerouslySetInnerHTML={{ __html: formattedHtml }}
            ></span>
            {showReviewLinks && (
              <span className="inline-flex items-center">
                <ReviewLinks
                  name={locationInfo.name}
                  location={destination}
                  coordinates={locationInfo.lat && locationInfo.lng ? { lat: locationInfo.lat, lng: locationInfo.lng } : null}
                  compact={true}
                />
              </span>
            )}
          </span>

          {/* Time input - only show for locations WITH coordinates */}
          {/* Anyone can set time for non-visited, only owner can modify time for visited */}
          {showAttractionControls && onAttractionStatusUpdate && locationInfo.lat && locationInfo.lng && (
            <div className="flex items-center gap-1 ml-2">
              <FaClock className={`text-sm ${isVisited && !isOwner ? 'text-neutral-300' : 'text-neutral-400'}`} />
              <input
                type="time"
                value={customTime}
                onChange={(e) => {
                  if (!isVisited || isOwner) {
                    onAttractionStatusUpdate(
                      dayNumber,
                      locationInfo.name,
                      { lat: locationInfo.lat, lng: locationInfo.lng },
                      { custom_time: e.target.value }
                    );
                  }
                }}
                disabled={isVisited && !isOwner}
                className={`px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                  isVisited && !isOwner
                    ? 'border-neutral-200 bg-neutral-100 text-neutral-400 cursor-not-allowed'
                    : 'border-neutral-300'
                }`}
                title={
                  isVisited
                    ? isOwner
                      ? 'Modify visit time'
                      : 'Time set for visited attraction (only owner can modify)'
                    : 'Set planned visit time'
                }
              />
            </div>
          )}
        </span>
        {/* Explore button - show between consecutive locations with coordinates */}
        {showExploreButton && (
          <button
            onClick={() => onExplore(
              locationInfo.name,
              nextLoc.name,
              { lat: locationInfo.lat, lng: locationInfo.lng },
              { lat: nextLoc.lat, lng: nextLoc.lng }
            )}
            className="flex items-center gap-1.5 mt-2 ml-6 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-full border border-amber-200 transition-colors group"
          >
            <FaCompass className="text-amber-500 group-hover:rotate-45 transition-transform" />
            <span>Explore between here and {nextLoc.name.length > 25 ? nextLoc.name.substring(0, 25) + '...' : nextLoc.name}</span>
          </button>
        )}
      </div>
    );
  };

  const formatText = (text) => {
    let formatted = text;
    // Remove "Coordinates: N/A" entirely
    formatted = formatted.replace(/\s*Coordinates:\s*N\/A/gi, '');
    // Remove "Coordinates:" label but keep the actual coordinates
    formatted = formatted.replace(/\s*Coordinates:\s*(\([-\d.,\s]+\))/gi, ' $1');
    // Remove "More Info" markdown links before converting (they're shown in AttractionCard)
    formatted = formatted.replace(/\[More\s*Info\]\([^)]+\)/gi, '');
    formatted = formatted.replace(/\|\s*\[More\s*Info\]\([^)]+\)/gi, '');
    // Convert markdown links
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-700 underline font-medium">$1</a>'
    );
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-neutral-800">$1</strong>');
    // Remove any remaining "More Info" text (plain text or after link conversion)
    formatted = formatted.replace(/<a[^>]*>\s*More\s*Info\s*<\/a>/gi, '');
    formatted = formatted.replace(/\bMore\s*Info\b/gi, '');
    // Clean up extra whitespace and pipes
    formatted = formatted.replace(/\s*\|\s*\|\s*/g, ' | ');
    formatted = formatted.replace(/\s*\|\s*$/g, '');
    formatted = formatted.replace(/^\s*\|\s*/g, '');
    return sanitizeHtml(formatted);
  };

  const formatLine = (line, index) => {
    const trimmedLine = line.trim();

    if (!trimmedLine || trimmedLine.match(/^[-=_]{3,}$/)) {
      return null;
    }

    // Skip lines that are booking link metadata (these are consumed by AttractionCard)
    // Matches: [Book on Booking.com](url) or similar booking links
    if (trimmedLine.match(/^\[Book[^\]]*\]\([^)]+\)$/i)) {
      return null;
    }

    // Skip lines that are just a booking.com URL
    if (trimmedLine.match(/^https?:\/\/www\.booking\.com/i) && !trimmedLine.includes('**')) {
      return null;
    }

    // Skip lines that are just "[More Info](url)" links - these are shown in the card
    if (trimmedLine.match(/^\[More\s*Info\]\([^)]+\)$/i)) {
      return null;
    }

    // Skip lines that start with "[More Info]" followed by other links
    if (trimmedLine.match(/^\[More\s*Info\]\([^)]+\)\s*(\||\s*\[)/i)) {
      return null;
    }

    // Skip lines that are just "More Info" text (plain or as link text)
    if (trimmedLine.match(/^More\s*Info\.?$/i)) {
      return null;
    }

    // Skip lines that contain only a More Info link (various formats)
    if (trimmedLine.match(/^\[?More\s*Info\]?(\([^)]*\))?\.?$/i)) {
      return null;
    }

    // Skip lines that are primarily "More Info" links with optional separators
    if (trimmedLine.match(/^[\|\s]*\[?More\s*Info\]?(\([^)]*\))?[\|\s]*$/i)) {
      return null;
    }

    // Skip lines where "More Info" is the only meaningful content
    const withoutMoreInfo = trimmedLine.replace(/\[?More\s*Info\]?(\([^)]*\))?/gi, '').replace(/[\|\s\-\*]+/g, '').trim();
    if (withoutMoreInfo.length === 0 && trimmedLine.match(/More\s*Info/i)) {
      return null;
    }

    // Skip lines that appear to be accommodation descriptions (following an accommodation line)
    // These are plain text lines that contain phrases typical of accommodation descriptions
    // Check if this looks like a rating format location: Name (X.X/5) (lat, lng)
    const isRatingFormatLocation = trimmedLine.match(/\(\d(?:\.\d)?\s*\/\s*5\)\s*\(-?\d+\.\d+,\s*-?\d+\.\d+\)/);
    const isPlainTextLine = (
      !trimmedLine.startsWith('**') &&
      !trimmedLine.startsWith('#') &&
      !trimmedLine.startsWith('-') &&
      !trimmedLine.startsWith('*') &&
      !trimmedLine.startsWith('•') &&
      !trimmedLine.match(/^\d+\./) &&
      !trimmedLine.match(/^[A-Z][a-z]+\s*\(/) && // Not a location with coords
      !isRatingFormatLocation && // Not a rating format location
      trimmedLine.length > 10 &&
      trimmedLine.length < 400
    );

    const hasAccommodationKeywords = trimmedLine.match(/\b(hotel|resort|inn|lodge|motel|hostel|accommodation|room|suite|stay|comfort|amenities|breakfast|pool|spa|wifi|parking|rooftop|bar|restaurant|gym|fitness|view|location|walking distance|central|downtown|modern|comfortable|cozy|relaxing|excellent|perfect|ideal|recommended|rated|reviews?|guests?|facilities|service)\b/i);

    // Check if this line is likely an accommodation description by looking at context
    if (isPlainTextLine && index > 0) {
      const lines = content ? content.split('\n') : [];
      let inWhereToStaySection = false;

      // First pass: check if we're in a "Where to Stay" section
      for (let i = index - 1; i >= Math.max(0, index - 8); i--) {
        const prevLine = lines[i]?.trim() || '';
        if (prevLine.match(/where\s*to\s*stay/i) || prevLine.match(/🏨/) || prevLine.match(/accommodation.*:/i)) {
          inWhereToStaySection = true;
          break;
        }
        // Stop if we hit a major section boundary
        if (prevLine.match(/^Day\s+\d+/i) || prevLine.match(/^(Morning|Afternoon|Evening)\s*:/i)) {
          break;
        }
      }

      // If in "Where to Stay" section, skip this description line
      if (inWhereToStaySection && hasAccommodationKeywords) {
        return null;
      }

      // Second pass: look for nearby accommodation line
      for (let i = index - 1; i >= Math.max(0, index - 6); i--) {
        const prevLine = lines[i]?.trim() || '';

        // Found accommodation line by name pattern - skip this description
        if (ACCOMMODATION_PATTERN.test(prevLine) && prevLine.includes('**')) {
          return null;
        }

        // Found location with star rating (likely accommodation) - skip this description
        if (prevLine.includes('**') && (prevLine.match(/⭐|★|\d\s*-?\s*star/i))) {
          return null;
        }

        // Stop looking back if we hit a Day header or another major section
        if (prevLine.match(/^Day\s+\d+/i) || prevLine.match(/^#{1,2}\s/) || prevLine.match(/^(Morning|Afternoon|Evening|Night)\s*:/i)) {
          break;
        }

        // Stop if we hit another location with coordinates that's NOT in a "Where to Stay" section
        if (prevLine.match(/^\*\*[^*]+\*\*.*Coordinates:/i) && !ACCOMMODATION_PATTERN.test(prevLine) && !inWhereToStaySection) {
          break;
        }
      }
    }

    // Main heading (## or **heading**)
    if (trimmedLine.startsWith('##')) {
      return (
        <h3 key={index} className="text-2xl font-bold text-neutral-800 mt-8 mb-4 pb-2 border-b-2 border-primary-300">
          {trimmedLine.replace(/^##\s*/, '')}
        </h3>
      );
    }

    if (trimmedLine.match(/^\*\*(.+?)\*\*:?$/)) {
      const text = trimmedLine.replace(/^\*\*(.+?)\*\*:?$/, '$1');
      return <h3 key={index} className="text-2xl font-bold text-neutral-800 mt-8 mb-4 pb-2 border-b-2 border-primary-300">{text}</h3>;
    }

    // Day heading (Day 1:, Day 2:, etc.)
    if (trimmedLine.match(/^Day\s+\d+/i) || trimmedLine.match(/^\d+\.\s+Day\s+\d+/i)) {
      return (
        <h4 key={index} className="text-xl font-bold text-white bg-gradient-ocean px-4 py-3 rounded-lg mt-6 mb-4 shadow-medium day-heading">
          {trimmedLine}
        </h4>
      );
    }

    // Subheading with bold - check if it's a location
    if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
      const locationInfo = extractLocationInfo(trimmedLine);
      const formatted = formatText(trimmedLine);

      if (locationInfo) {
        return (
          <div key={index} className="w-full mt-4 mb-2">
            {renderLocationWithActions(locationInfo, formatted, `loc-${index}`, trimmedLine, index)}
          </div>
        );
      }

      return <div key={index} className="text-lg font-semibold text-neutral-700 mt-4 mb-2" dangerouslySetInnerHTML={{ __html: formatted }}></div>;
    }

    // Numbered list - check for locations with checkboxes
    if (trimmedLine.match(/^\d+\.\s/)) {
      const text = trimmedLine.replace(/^\d+\.\s*/, '');
      const formatted = formatText(text);
      const number = trimmedLine.match(/^\d+/)[0];

      // Check if this numbered item contains a location
      const locationInfo = extractLocationInfo(text);
      if (locationInfo) {
        // If location has coordinates (will render AttractionCard), use full width
        if (locationInfo.lat && locationInfo.lng) {
          return (
            <div key={index} className="w-full mb-2">
              {renderLocationWithActions(locationInfo, formatted, `num-loc-${index}`, text, index)}
            </div>
          );
        }
        return (
          <div key={index} className="flex gap-3 mb-2 ml-4 items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">
              {number}
            </span>
            {renderLocationWithActions(locationInfo, formatted, `num-loc-${index}`, text, index)}
          </div>
        );
      }

      return (
        <div key={index} className="flex gap-3 mb-2 ml-4">
          <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
            {number}
          </span>
          <span className="text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Checklist item - handles both [ ] and [x] patterns with any bullet type (-, *, •)
    if (trimmedLine.match(/^[-*•]\s*\[[\sxX]?\]\s*/)) {
      const text = trimmedLine.replace(/^[-*•]\s*\[[\sxX]?\]\s*/, '');
      const formatted = formatText(text);
      const itemKey = text.trim().toLowerCase();
      const isChecked = !!checkedItems[itemKey];

      // Check if this checklist item contains a location
      const locationInfo = extractLocationInfo(text);
      if (locationInfo) {
        // If location has coordinates (will render AttractionCard), use full width
        if (locationInfo.lat && locationInfo.lng) {
          return (
            <div key={index} className="w-full mb-2">
              {renderLocationWithActions(locationInfo, formatted, `check-loc-${index}`, text, index)}
            </div>
          );
        }
        return (
          <div key={index} className={`flex items-start gap-3 mb-2 ml-4 ${isChecked ? 'opacity-60' : ''}`}>
            {showCheckboxes && (
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggleCheck && onToggleCheck(itemKey)}
                className="flex-shrink-0 w-5 h-5 rounded border-2 border-neutral-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 accent-green-500"
              />
            )}
            {!showCheckboxes && <span className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mt-2"></span>}
            <div className={`flex-1 ${isChecked ? 'line-through text-neutral-400' : ''}`}>{renderLocationWithActions(locationInfo, formatted, `check-loc-${index}`, text, index)}</div>
          </div>
        );
      }

      return (
        <div key={index} className={`flex items-start gap-3 mb-2 ml-4 ${isChecked ? 'opacity-60' : ''}`}>
          {showCheckboxes && (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => onToggleCheck && onToggleCheck(itemKey)}
              className="flex-shrink-0 w-5 h-5 rounded border-2 border-neutral-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 accent-green-500"
            />
          )}
          {!showCheckboxes && <span className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mt-2"></span>}
          <span className={`flex-1 text-neutral-700 ${isChecked ? 'line-through text-neutral-400' : ''}`} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Bullet point - check for locations with checkboxes
    if (trimmedLine.match(/^[-*•]\s/)) {
      let text = trimmedLine.replace(/^[-*•]\s*/, '');
      // Strip any leftover [ ] or [x] markers when showing checkboxes
      if (showCheckboxes) {
        text = text.replace(/^\[[\sxX]?\]\s*/, '');
      }
      const formatted = formatText(text);
      const itemKey = text.trim().toLowerCase();
      const isChecked = !!checkedItems[itemKey];

      // Check if this bullet point contains a location
      const locationInfo = extractLocationInfo(text);
      if (locationInfo) {
        // If location has coordinates and we're in a day section, AttractionCard will be used
        // Don't show bullet in that case as the card has its own styling
        if (locationInfo.lat && locationInfo.lng) {
          return (
            <div key={index} className="w-full mb-2">
              {renderLocationWithActions(locationInfo, formatted, `bullet-loc-${index}`, text, index)}
            </div>
          );
        }
        return (
          <div key={index} className={`flex gap-3 mb-2 ml-4 items-start ${isChecked ? 'opacity-60' : ''}`}>
            {showCheckboxes ? (
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggleCheck && onToggleCheck(itemKey)}
                className="flex-shrink-0 w-5 h-5 rounded border-2 border-neutral-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 accent-green-500"
              />
            ) : (
              <span className="flex-shrink-0 w-2 h-2 bg-secondary-400 rounded-full mt-3"></span>
            )}
            <div className={`flex-1 ${isChecked ? 'line-through text-neutral-400' : ''}`}>{renderLocationWithActions(locationInfo, formatted, `bullet-loc-${index}`, text, index)}</div>
          </div>
        );
      }

      return (
        <div key={index} className={`flex gap-3 mb-2 ml-4 ${isChecked ? 'opacity-60' : ''}`}>
          {showCheckboxes ? (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => onToggleCheck && onToggleCheck(itemKey)}
              className="flex-shrink-0 w-5 h-5 rounded border-2 border-neutral-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 accent-green-500"
            />
          ) : (
            <span className="flex-shrink-0 w-2 h-2 bg-secondary-400 rounded-full mt-2"></span>
          )}
          <span className={`text-neutral-700 leading-relaxed ${isChecked ? 'line-through text-neutral-400' : ''}`} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Time-based item - check for locations with checkboxes
    if (trimmedLine.match(/^\d{1,2}:\d{2}\s*(AM|PM|am|pm)?/)) {
      const formatted = formatText(trimmedLine);

      // Check if this time-based item contains a location
      const locationInfo = extractLocationInfo(trimmedLine);
      if (locationInfo) {
        // If location has coordinates (will render AttractionCard), use full width
        if (locationInfo.lat && locationInfo.lng) {
          return (
            <div key={index} className="w-full mb-3">
              {renderLocationWithActions(locationInfo, formatted, `time-loc-${index}`, trimmedLine, index)}
            </div>
          );
        }
        return (
          <div key={index} className="flex gap-3 mb-3 ml-4 p-3 bg-accent-50 rounded-lg items-start">
            <span className="text-2xl">🕐</span>
            {renderLocationWithActions(locationInfo, formatted, `time-loc-${index}`, trimmedLine, index)}
          </div>
        );
      }

      return (
        <div key={index} className="flex gap-3 mb-3 ml-4 p-3 bg-accent-50 rounded-lg">
          <span className="text-2xl">🕐</span>
          <span className="text-neutral-700 font-medium" dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Standalone checkbox line without bullet prefix (e.g. "[ ] item" or "[x] item")
    if (showCheckboxes && trimmedLine.match(/^\[[\sxX]?\]\s+/)) {
      const text = trimmedLine.replace(/^\[[\sxX]?\]\s*/, '');
      const formatted = formatText(text);
      const itemKey = text.trim().toLowerCase();
      const isChecked = !!checkedItems[itemKey];
      return (
        <div key={index} className={`flex items-start gap-3 mb-2 ml-4 ${isChecked ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => onToggleCheck && onToggleCheck(itemKey)}
            className="flex-shrink-0 w-5 h-5 rounded border-2 border-neutral-300 text-green-500 focus:ring-green-500 cursor-pointer mt-0.5 accent-green-500"
          />
          <span className={`flex-1 text-neutral-700 ${isChecked ? 'line-through text-neutral-400' : ''}`} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Regular paragraph - check for locations with checkboxes
    const formatted = formatText(trimmedLine);

    // Check if this paragraph contains a location
    const locationInfo = extractLocationInfo(trimmedLine);
    if (locationInfo) {
      // If location has coordinates (will render AttractionCard), use full width
      if (locationInfo.lat && locationInfo.lng) {
        return (
          <div key={index} className="w-full mb-3">
            {renderLocationWithActions(locationInfo, formatted, `para-loc-${index}`, trimmedLine, index)}
          </div>
        );
      }
      return (
        <div key={index} className="text-neutral-700 leading-relaxed mb-3">
          {renderLocationWithActions(locationInfo, formatted, `para-loc-${index}`, trimmedLine, index)}
        </div>
      );
    }

    return <p key={index} className="text-neutral-700 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: formatted }}></p>;
  };

  // Split content into day sections if showing directions
  const splitIntoDays = (content) => {
    const lines = content.split('\n');
    const days = [];
    let currentDay = { heading: null, content: [], lineIndex: 0, isActualDay: false };
    let foundDayHeaders = false;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      // Check if this is a day heading (handles **Day 1, Day 1, # Day 1, ## Day 1, etc.)
      const isDayHeading = trimmedLine.match(/^(\*\*|#{1,3}\s*)?Day\s+\d+/i) || trimmedLine.match(/^\d+\.\s+Day\s+\d+/i);
      if (isDayHeading) {
        foundDayHeaders = true;
        // Save the previous day if it has content
        if (currentDay.heading || currentDay.content.length > 0) {
          days.push({ ...currentDay });
        }
        currentDay = { heading: line, content: [], lineIndex: index, isActualDay: true };
      } else {
        currentDay.content.push({ line, index });
      }
    });

    // Don't forget the last day
    if (currentDay.heading || currentDay.content.length > 0) {
      // If no day headers were found in the entire content, treat the content as a single day
      // so that directions still show for short trips without explicit day breakdowns
      if (!foundDayHeaders) {
        currentDay.isActualDay = true;
      }
      days.push(currentDay);
    }

    return days;
  };

  if (showDirections) {
    const days = splitIntoDays(content);
    // Extract GPS coordinates from itinerary content and check against OSRM bounding boxes
    const allLocations = extractLocationsWithCoordinates(content);
    const hasCoordinates = allLocations.length > 0;
    // Check if destination is in a supported region (only meaningful if we have coordinates)
    const isInSupportedRegion = hasCoordinates &&
      allLocations.some(loc => isCoordinateInSupportedRegion(loc.lat, loc.lng));
    // For backward compatibility, destinationSupported is true if in supported region
    // But we'll show DayDirections even without coordinates (it handles that case gracefully)
    const destinationSupported = isInSupportedRegion;

    return (
      <div className="prose prose-neutral max-w-none">
        {/* Navigation info - Google Maps works worldwide */}

        {days.map((day, dayIndex) => {
          // Extract day number from heading for weather lookup
          let dayNum = null;
          if (day.heading) {
            const dayNumMatch = day.heading.match(/Day\s+(\d+)/i);
            if (dayNumMatch) {
              dayNum = dayNumMatch[1];
            }
          }
          const dayWeatherInfo = dayNum && weatherData && weatherData.forecasts
            ? weatherData.forecasts[dayNum]
            : null;

          // Extract city from day heading (e.g., "Day 3: Agra" -> "Agra")
          const dayHeadingCity = day.heading?.match(/Day\s+\d+[^:]*:\s*(.+)/i)?.[1]?.replace(/\*\*/g, '').trim();

          return (
          <div key={dayIndex}>
            {/* Render each day section with its own FormattedContent that knows if it's a day section */}
            <FormattedContent
              content={(day.heading || '') + '\n' + day.content.map(c => c.line).join('\n')}
              showCheckboxes={showCheckboxes}
              showDirections={false}
              isOwner={isOwner}
              destination={dayHeadingCity || destination}
              requestId={requestId}
              cachedDirections={cachedDirections}
              onDirectionsCached={onDirectionsCached}
              isInDaySection={day.isActualDay}
              dayNumber={dayNum ? parseInt(dayNum) : dayIndex + 1}
              tripStartDate={tripStartDate}
              attractionStatuses={attractionStatuses}
              onAttractionStatusUpdate={onAttractionStatusUpdate}
              showAttractionControls={showAttractionControls}
              onExplore={onExplore}
              checkedItems={checkedItems}
              onToggleCheck={onToggleCheck}
            />

            {/* Weather forecast card — shown between content and directions */}
            {day.isActualDay && dayNum && (
              <DayWeather
                dayNumber={parseInt(dayNum, 10)}
                weatherData={dayWeatherInfo}
                loading={weatherLoading}
                error={null}
              />
            )}

            {/* Render directions for actual day sections - now works worldwide via Google Maps links */}
            {day.isActualDay && (
              <DayDirections
                content={(day.heading || '') + '\n' + day.content.map(c => c.line).join('\n')}
                requestId={requestId}
                flights={flights}
                dayHeading={day.heading}
              />
            )}
          </div>
        );
        })}
      </div>
    );
  }

  const lines = content.split('\n');

  return (
    <div className="prose prose-neutral max-w-none">
      {lines.map((line, index) => formatLine(line, index)).filter(element => element !== null)}
    </div>
  );
};

// PDF-optimized content formatter with inline styles
const PDFFormattedContent = ({ content, isChecklist = false }) => {
  const formatText = (text) => {
    let formatted = text;
    // Remove "Coordinates: N/A" entirely
    formatted = formatted.replace(/\s*Coordinates:\s*N\/A/gi, '');
    // Remove "Coordinates:" label but keep the actual coordinates
    formatted = formatted.replace(/\s*Coordinates:\s*(\([-\d.,\s]+\))/gi, ' $1');
    // Convert markdown links to plain text (links don't work in PDF anyway)
    formatted = formatted.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    // Convert bold markdown to styled spans
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 700; color: #1e293b;">$1</strong>');
    return sanitizeHtml(formatted);
  };

  const formatLine = (line, index) => {
    const trimmedLine = line.trim();

    if (!trimmedLine || trimmedLine.match(/^[-=_]{3,}$/)) {
      return null;
    }

    // Skip lines that are booking link metadata (these are consumed by AttractionCard)
    if (trimmedLine.match(/^\[Book[^\]]*\]\([^)]+\)$/i)) {
      return null;
    }

    // Skip lines that are just a booking.com URL
    if (trimmedLine.match(/^https?:\/\/www\.booking\.com/i) && !trimmedLine.includes('**')) {
      return null;
    }

    // Main heading (## or **heading**)
    if (trimmedLine.startsWith('##')) {
      return (
        <h3 key={index} style={{
          fontSize: '14pt',
          fontWeight: 'bold',
          color: '#1e293b',
          marginTop: '24px',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '2px solid #0ea5e9'
        }}>
          {trimmedLine.replace(/^##\s*/, '')}
        </h3>
      );
    }

    if (trimmedLine.match(/^\*\*(.+?)\*\*:?$/)) {
      const text = trimmedLine.replace(/^\*\*(.+?)\*\*:?$/, '$1');
      return (
        <h3 key={index} style={{
          fontSize: '14pt',
          fontWeight: 'bold',
          color: '#1e293b',
          marginTop: '24px',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '2px solid #0ea5e9'
        }}>
          {text}
        </h3>
      );
    }

    // Day heading (Day 1:, Day 2:, etc.)
    if (trimmedLine.match(/^Day\s+\d+/i) || trimmedLine.match(/^\d+\.\s+Day\s+\d+/i)) {
      return (
        <h4 key={index} className="pdf-day-heading" style={{
          fontSize: '13pt',
          fontWeight: 'bold',
          color: 'white',
          background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
          padding: '12px 16px',
          borderRadius: '8px',
          marginTop: '20px',
          marginBottom: '12px',
          pageBreakBefore: 'auto'
        }}>
          {trimmedLine}
        </h4>
      );
    }

    // Subheading with bold
    if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
      const formatted = formatText(trimmedLine);
      return (
        <div key={index} className="pdf-no-break" style={{
          fontSize: '12pt',
          fontWeight: '600',
          color: '#334155',
          marginTop: '12px',
          marginBottom: '8px'
        }} dangerouslySetInnerHTML={{ __html: formatted }}></div>
      );
    }

    // Numbered list
    if (trimmedLine.match(/^\d+\.\s/)) {
      const text = trimmedLine.replace(/^\d+\.\s*/, '');
      const formatted = formatText(text);
      const number = trimmedLine.match(/^\d+/)[0];
      return (
        <div key={index} className="pdf-no-break" style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '8px',
          marginLeft: '12px'
        }}>
          <span style={{
            flexShrink: 0,
            width: '20px',
            height: '20px',
            backgroundColor: '#0ea5e9',
            color: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9pt',
            fontWeight: 'bold'
          }}>
            {number}
          </span>
          <span style={{ color: '#374151', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Checklist item
    if (trimmedLine.match(/^-\s*\[\s*\]\s*/)) {
      const text = trimmedLine.replace(/^-\s*\[\s*\]\s*/, '');
      const formatted = formatText(text);
      return (
        <div key={index} className="pdf-no-break" style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '8px',
          marginLeft: '12px'
        }}>
          <span style={{
            flexShrink: 0,
            width: '14px',
            height: '14px',
            border: '2px solid #22c55e',
            borderRadius: '3px',
            marginTop: '3px'
          }}></span>
          <span style={{ color: '#374151' }} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Bullet point
    if (trimmedLine.match(/^[-*•]\s/)) {
      const text = trimmedLine.replace(/^[-*•]\s*/, '');
      const formatted = formatText(text);
      return (
        <div key={index} className="pdf-no-break" style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '8px',
          marginLeft: '12px'
        }}>
          <span style={{
            flexShrink: 0,
            width: '6px',
            height: '6px',
            backgroundColor: '#06b6d4',
            borderRadius: '50%',
            marginTop: '8px'
          }}></span>
          <span style={{ color: '#374151', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Time-based item
    if (trimmedLine.match(/^\d{1,2}:\d{2}\s*(AM|PM|am|pm)?/)) {
      const formatted = formatText(trimmedLine);
      return (
        <div key={index} className="pdf-no-break" style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '10px',
          marginLeft: '12px',
          padding: '10px',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          borderLeft: '3px solid #f59e0b'
        }}>
          <span style={{ fontSize: '14pt' }}>🕐</span>
          <span style={{ color: '#374151', fontWeight: '500' }} dangerouslySetInnerHTML={{ __html: formatted }}></span>
        </div>
      );
    }

    // Regular paragraph
    const formatted = formatText(trimmedLine);
    return (
      <p key={index} style={{
        color: '#374151',
        lineHeight: '1.6',
        marginBottom: '10px'
      }} dangerouslySetInnerHTML={{ __html: formatted }}></p>
    );
  };

  const lines = content.split('\n');

  return (
    <div>
      {lines.map((line, index) => formatLine(line, index)).filter(element => element !== null)}
    </div>
  );
};

export default ItineraryDisplay;
